import React, { useEffect, useMemo, useState } from "react";
import {
  Edit as EditIcon,
  Trash2 as TrashIcon,
  RotateCcw as RestoreIcon,
  KeyRound,
  LogIn,
  Search,
  X,
  Download,
  Upload,
  Plus,
} from "lucide-react";
import {
  DirectionsCarFilled as FleetVehicleIcon,
  PersonAddAlt as FleetUserAssignIcon,
  WorkOutline as ManagerRoleIcon,
  AssignmentInd as HrRoleIcon,
  Badge as OfficeRoleIcon,
  Engineering as FieldRoleIcon,
  AdminPanelSettings as AdminRoleIcon,
  Apartment as DealerRoleIcon,
  PersonOutline as BasicUserRoleIcon,
  Security as PermissionIcon,
  PersonSearch as ViewerRoleIcon,
  SupportAgent as ClientRoleIcon,
  GroupOutlined as TotalUsersStatIcon,
  PersonOffOutlined as InactiveUsersStatIcon,
  ShieldOutlined as ActiveUsersStatIcon,
  AdminPanelSettingsOutlined as AdminUsersStatIcon,
  PersonAddAlt1Outlined as NewUsersStatIcon,
  TuneRounded as FiltersToolbarIcon,
  RefreshRounded as RefreshToolbarIcon,
  NotificationsActiveOutlined as NotificationsActiveOutlinedIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
  VisibilityOffOutlined as VisibilityOffOutlinedIcon,
} from "@mui/icons-material";

import { ROLES, getUserActionsEffective } from "../../utils/rbacHelper";
import FlexibleStatsRow from "../common/FlexibleStatsRow";

// ✅ logic imports
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useImpersonateUserMutation,
  useEditDeviceMutation,
  useEditUserMutation,
  useRevealUserPasswordMutation,
} from "../../redux/admin/adminApi";
import { loginSuccess } from "../../store/authSlice";
import { adminApi } from "../../redux/admin/adminApi";
import { useStatus } from "../../popup/NotificationContext";

// ✅ MUI
import {
  Box,
  Stack,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Switch,
  FormControlLabel,
  FormGroup,
  Grid,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TableContainer,
  Tabs,
  Tab,
  useMediaQuery,
  Menu,
  CircularProgress,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
} from "recharts";
import { downloadEntityTemplate, uploadEntityFile } from "../../services/importExportService";
import ScrollableTableContainer from "../common/ScrollableTableContainer";
import CreateUserModal from "./CreateUserModal";
import AddDealerModal from "../modals/AddDealerModal";
import AddSubAdminModal from "../modals/AddSubAdminModal";
import AddUserModal from "../modals/AddUserModal";
import ManagedUserNotificationDialog from "../modals/ManagedUserNotificationDialog";

/** Mongo extended JSON unwrapping */
const unwrapMongoValue = (v) => {
  if (v && typeof v === "object" && !Array.isArray(v)) {
    if ("$oid" in v) return v.$oid;
    if ("$date" in v) return v.$date;
  }
  return v;
};

const normalizeEntityId = (value) =>
  String(
    unwrapMongoValue(
      value?._id ||
        value?.id ||
        value
    ) || ""
  );

const isProbablyDate = (v, key = "") => {
  const val = unwrapMongoValue(v);
  if (!val) return false;

  const k = String(key).toLowerCase();
  if (k.includes("date") || k.includes("time") || k.endsWith("at")) return true;

  if (typeof val === "string") {
    const t = Date.parse(val);
    return Number.isFinite(t) && val.length >= 8;
  }
  if (val instanceof Date) return true;
  return false;
};

const permissionLabelFromKey = (key) =>
  String(key)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const collectPermissionLabels = (permissions = {}) => {
  const labels = [];

  Object.entries(permissions || {}).forEach(([groupKey, groupValue]) => {
    if (groupKey === "limits" || !groupValue || typeof groupValue !== "object") return;

    Object.entries(groupValue).forEach(([itemKey, itemValue]) => {
      if (itemValue === true) {
        labels.push(`${permissionLabelFromKey(groupKey)}: ${permissionLabelFromKey(itemKey)}`);
      }
    });
  });

  const deviceLimit = Number(permissions?.limits?.deviceLimit || 0);
  const userLimit = Number(permissions?.limits?.userLimit || 0);

  if (deviceLimit > 0) labels.push(`Device Limit: ${deviceLimit}`);
  if (userLimit > 0) labels.push(`User Limit: ${userLimit}`);

  return labels;
};

const getInitials = (name) => {
  if (!name) return "U";
  return name
    .split(" ")
    .map((s) => s[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const resolveUserBucket = (user) => {
  return (
    user?.department ||
    user?.roleType ||
    user?.designation ||
    user?.team ||
    user?.group ||
    user?.attributes?.find?.((item) => item?.key === "department" || item?.label === "Department")?.value ||
    "-"
  );
};

const resolveCreatedByLabel = (user) => {
  const createdBy = user?.createdBy;
  if (!createdBy) return "-";
  if (typeof createdBy === "string") return createdBy;
  return createdBy?.username || createdBy?.name || createdBy?.email || createdBy?.uniqueId || createdBy?._id || "-";
};

const roleLabel = (role) => {
  const normalized = normalizeRole(role);
  return CREATE_ROLE_CATALOG[normalized]?.title || (normalized === ROLES.DEALER ? "Dealer" : normalized);
};

const CREATE_ROLE_CATALOG = {
  [ROLES.SUB_ADMIN]: {
    title: "Admin",
    description: "Admin users who manage operational access and user controls.",
    icon: AdminRoleIcon,
    tone: "#2563eb",
  },
  [ROLES.DEALER]: {
    title: "Dealer",
    description: "Dealer accounts with customer and license distribution access.",
    icon: DealerRoleIcon,
    tone: "#0f766e",
  },
  [ROLES.USER]: {
    title: "User",
    description: "Standard users for day-to-day tracking access.",
    icon: BasicUserRoleIcon,
    tone: "#475569",
  },
  [ROLES.DRIVER]: {
    title: "Driver",
    description: "Operational driver and assigned user accounts.",
    icon: ClientRoleIcon,
    tone: "#ea580c",
  },
  [ROLES.CLIENT]: {
    title: "Client",
    description: "Customer-facing tracking accounts and shared access.",
    icon: ClientRoleIcon,
    tone: "#ea580c",
  },
  [ROLES.VIEWER]: {
    title: "Viewer",
    description: "Read-only monitoring access for shared visibility.",
    icon: ViewerRoleIcon,
    tone: "#0ea5e9",
  },
  [ROLES.MANAGER]: {
    title: "Manager",
    description: "Full management access for operations and teams.",
    icon: ManagerRoleIcon,
    tone: "#2563eb",
  },
  [ROLES.HR]: {
    title: "HR",
    description: "People operations and employee coordination access.",
    icon: HrRoleIcon,
    tone: "#db2777",
  },
  [ROLES.OFFICE_EMPLOYEE]: {
    title: "Office Employee",
    description: "Back-office workflow and coordination access.",
    icon: OfficeRoleIcon,
    tone: "#7c3aed",
  },
  [ROLES.FIELD_EMPLOYEE]: {
    title: "Field Employee",
    description: "Ground operations and field execution access.",
    icon: FieldRoleIcon,
    tone: "#16a34a",
  },
};

const normalizeRole = (role) => {
  const r = String(role || "").trim().toLowerCase();
  if (r === "superadmin" || r === "super_admin") return ROLES.SUPER_ADMIN;
  if (r === "groupadmin" || r === "dealer") return ROLES.DEALER;
  if (r === "subadmin") return ROLES.SUB_ADMIN;
  if (r === "officeemployee") return ROLES.OFFICE_EMPLOYEE;
  if (r === "fieldemployee") return ROLES.FIELD_EMPLOYEE;
  return r;
};

const matchesManagedUserId = (candidate, userId) => {
  if (!userId || !candidate) return false;
  return String(candidate?._id || candidate?.id || candidate) === String(userId);
};

const isDeviceOwnedByUser = (device, userId) => {
  if (!userId || !device) return false;
  return [
    device?.assignedToUser,
    device?.adminId,
    device?.createdBy,
    device?.subAdminOwner,
    device?.dealerOwner,
    device?.dealer,
  ].some((candidate) => matchesManagedUserId(candidate, userId));
};

/** ✅ Remaining tokens: prefer license summary, fallback to legacy credits if needed */
const CreditsCell = ({ credits, licenseSummary, remainingTokens }) => {
  const c = unwrapMongoValue(credits) || {};
  const summary = unwrapMongoValue(licenseSummary) || {};
  const balance = Number(
    remainingTokens ??
      summary.totalRemainingCredits ??
      c.balance ??
      0
  );

  return (
    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
      <Chip size="small" label={`Remaining Tokens: ${balance}`} sx={{ fontWeight: 900, borderRadius: 2 }} />
    </Stack>
  );
};

const ANALYTICS_ROLE_ORDER = [
  ROLES.SUB_ADMIN,
  ROLES.DEALER,
  ROLES.MANAGER,
  ROLES.HR,
  ROLES.OFFICE_EMPLOYEE,
  ROLES.FIELD_EMPLOYEE,
  ROLES.DRIVER,
  ROLES.CLIENT,
  ROLES.VIEWER,
  ROLES.USER,
];

const ANALYTICS_COLORS = ["#2563eb", "#22c55e", "#f59e0b", "#a855f7", "#ec4899", "#94a3b8"];

const getRoleMeta = (role) => {
  const normalized = normalizeRole(role);
  const fallback = {
    title: roleLabel(normalized),
    description: "User account",
    icon: BasicUserRoleIcon,
    tone: "#2563eb",
  };
  return CREATE_ROLE_CATALOG[normalized] || fallback;
};

const formatShortDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTimeCompact = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const formatRelativeShort = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.max(0, Math.floor(diffMs / 60000));
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hr ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
};

const getStatusMeta = (rawStatus) => {
  const normalized = String(rawStatus || "").trim().toLowerCase().replace(/\s+/g, "_");
  if (normalized.includes("run")) {
    return { key: "running", label: "Running", color: "#16a34a", bg: alpha("#16a34a", 0.12) };
  }
  if (normalized.includes("stop")) {
    return { key: "stopped", label: "Stopped", color: "#ef4444", bg: alpha("#ef4444", 0.12) };
  }
  if (normalized.includes("no_report") || normalized.includes("noreport") || normalized.includes("offline")) {
    return { key: "no_reporting", label: "No Reporting", color: "#64748b", bg: alpha("#64748b", 0.12) };
  }
  if (normalized.includes("idle")) {
    return { key: "idle", label: "Idle", color: "#f59e0b", bg: alpha("#f59e0b", 0.12) };
  }
  return { key: "inactive", label: "Inactive", color: "#94a3b8", bg: alpha("#94a3b8", 0.16) };
};

const resolveDeviceLastUpdate = (device) =>
  device?.lastDataReceivedAt ||
  device?.updatedAt ||
  device?.lastActivityAt ||
  device?.createdAt ||
  "";

const resolveDeviceSimNo = (device) =>
  device?.msdn ||
  device?.MSDN ||
  device?.simNo ||
  device?.simCard?.MSDN ||
  "-";

const resolveDeviceVehicleLabel = (device) =>
  device?.name ||
  device?.vehicleNumber ||
  device?.deviceId ||
  device?.imei ||
  "-";

const buildSparklineSeries = (seed, points = 10) =>
  Array.from({ length: points }, (_, index) => {
    const wave = Math.sin((index + 1) * 0.9 + seed) * 4;
    const drift = ((index % 3) - 1) * 1.4;
    return {
      value: Math.max(2, Math.round(seed * 0.55 + index * 0.9 + wave + drift + 6)),
    };
  });

const StatSparkline = ({ data, color }) => (
  <Box sx={{ width: "100%", height: 40, mt: 1.2 }}>
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data}>
        <defs>
          <linearGradient id={`spark-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          type="monotone"
          dataKey="value"
          stroke={color}
          fill={`url(#spark-${color.replace("#", "")})`}
          strokeWidth={2}
          dot={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  </Box>
);

const AnalyticsStatCard = ({ icon, label, value, caption, color, sparkline }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2.2,
      borderRadius: 4,
      borderColor: alpha(color, 0.12),
      background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
      boxShadow: "0 18px 48px rgba(15, 23, 42, 0.06)",
      height: "100%",
    }}
  >
    <Stack direction="row" spacing={1.6} alignItems="flex-start">
      <Avatar
        sx={{
          width: 54,
          height: 54,
          bgcolor: alpha(color, 0.12),
          color,
        }}
      >
        {icon}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}>
          {label}
        </Typography>
        <Typography sx={{ mt: 0.2, fontSize: { xs: 28, md: 30 }, lineHeight: 1.05, fontWeight: 800, color: "#0f172a" }}>
          {value}
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 13, fontWeight: 600, color: "text.secondary" }}>
          {caption}
        </Typography>
      </Box>
    </Stack>
    <StatSparkline data={sparkline} color={color} />
  </Paper>
);

const FloatingCard = ({ title, action, children, height }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2.2,
      borderRadius: 4,
      borderColor: alpha("#0f172a", 0.08),
      background: "#fff",
      boxShadow: "0 18px 44px rgba(15, 23, 42, 0.05)",
      height: height || "100%",
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>{title}</Typography>
      {action}
    </Stack>
    {children}
  </Paper>
);

/** =========================
 * MAIN USER MANAGEMENT
 * ========================= */
function UserManagement({
  users,
  creatableRoles,
  manageableRoles,
  currentUser,
  currentUserId,
  currentUserRole,
  setEditingUser,
  handleDeleteUser,
  handleRestoreUser,
  handleRemoveManagedUser,
  setResetUserId,
  setShowResetPasswordModal,
  setShowCreateUserModal,
  canAssignDevice,
  canAssignUserToGA,
  assignmentLabels,
  handleAssignDevice,
  handleAssignUserToGroupAdmin,
  safeDevices = [],
  assignableUsersForDevices = [],
  assignableUsersForGroupAdmin = [],
  groupAdmins = [],
  deviceSearchQuery,
  setDeviceSearchQuery,
  assignDeviceId,
  setAssignDeviceId,
  userSearchQuery,
  setUserSearchQuery,
  assignToUserId,
  setAssignToUserId,
  assignUserSearchQuery,
  setAssignUserSearchQuery,
  assignUserToGroupAdminUserId,
  setAssignUserToGroupAdminUserId,
  groupAdminSearchQuery,
  setGroupAdminSearchQuery,
  assignUserToGroupAdminTargetId,
  setAssignUserToGroupAdminTargetId,
  onRefreshData,
  forcedRole = "",
  pageTitle = "",
  pageSubtitle = "",
  pageBreadcrumb = "",
  addButtonLabel = "Add User",
  directCreateRole = "",
  bucketLabel = "Department",
  entityPluralLabel = "Users",
  rolePageMode = false,
}) {
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const { showStatus } = useStatus();
  const [editDevice] = useEditDeviceMutation();
  const [editUser] = useEditUserMutation();
  const [revealUserPassword] = useRevealUserPasswordMutation();
  const actionButtonSx = (tone = "default") => {
    const tones = {
      default: {
        color: theme.palette.text.secondary,
        bg: alpha(theme.palette.text.primary, 0.04),
        hoverBg: alpha(theme.palette.text.primary, 0.1),
      },
      primary: {
        color: theme.palette.primary.main,
        bg: alpha(theme.palette.primary.main, 0.1),
        hoverBg: alpha(theme.palette.primary.main, 0.18),
      },
      success: {
        color: theme.palette.success.main,
        bg: alpha(theme.palette.success.main, 0.12),
        hoverBg: alpha(theme.palette.success.main, 0.2),
      },
      warning: {
        color: theme.palette.warning.dark,
        bg: alpha(theme.palette.warning.main, 0.14),
        hoverBg: alpha(theme.palette.warning.main, 0.22),
      },
      info: {
        color: theme.palette.info.main,
        bg: alpha(theme.palette.info.main, 0.12),
        hoverBg: alpha(theme.palette.info.main, 0.2),
      },
      error: {
        color: theme.palette.error.main,
        bg: alpha(theme.palette.error.main, 0.12),
        hoverBg: alpha(theme.palette.error.main, 0.2),
      },
    };

    const palette = tones[tone] || tones.default;
    return {
      color: palette.color,
      backgroundColor: palette.bg,
      border: `1px solid ${alpha(palette.color, 0.14)}`,
      transition: "all 0.18s ease",
      "&:hover": {
        backgroundColor: palette.hoverBg,
        transform: "translateY(-1px)",
      },
    };
  };
  const actionTileSx = (tone = "default") => {
    const tones = {
      primary: { color: "#ffffff", bg: "#2563eb", hover: "#1d4ed8", border: alpha("#2563eb", 0.35) },
      success: { color: "#ffffff", bg: "#10b981", hover: "#059669", border: alpha("#10b981", 0.35) },
      warning: { color: "#0f172a", bg: "#fbbf24", hover: "#f59e0b", border: alpha("#f59e0b", 0.4) },
      error: { color: "#ffffff", bg: "#ef4444", hover: "#dc2626", border: alpha("#ef4444", 0.38) },
      info: { color: "#ffffff", bg: "#3b82f6", hover: "#2563eb", border: alpha("#3b82f6", 0.34) },
      slate: { color: "#0f172a", bg: "#f8fafc", hover: "#e2e8f0", border: alpha("#0f172a", 0.08) },
      indigo: { color: "#ffffff", bg: "#4f46e5", hover: "#4338ca", border: alpha("#4f46e5", 0.34) },
    };

    const palette = tones[tone] || tones.slate;
    return {
      width: 42,
      height: 42,
      borderRadius: 1.8,
      border: `1px solid ${palette.border}`,
      backgroundColor: palette.bg,
      color: palette.color,
      boxShadow: `0 10px 24px ${alpha(palette.bg, 0.22)}`,
      transition: "transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease",
      "&:hover": {
        backgroundColor: palette.hover,
        transform: "translateY(-1px)",
        boxShadow: `0 14px 28px ${alpha(palette.bg, 0.3)}`,
      },
      "&.Mui-disabled": {
        color: alpha(palette.color, 0.55),
        backgroundColor: alpha(palette.bg, 0.32),
        boxShadow: "none",
      },
    };
  };

  const normalizedForcedRole = normalizeRole(forcedRole);
  const isRoleScopedPage = rolePageMode && Boolean(normalizedForcedRole);
  const roleScopedTitle = pageTitle || (isRoleScopedPage ? `${roleLabel(normalizedForcedRole)} Users` : "User Management");
  const roleScopedSubtitle =
    pageSubtitle || (isRoleScopedPage ? `Manage ${roleLabel(normalizedForcedRole).toLowerCase()} accounts, access and activity` : "Manage system users, roles and permissions");
  const roleScopedAddLabel = addButtonLabel || `Add ${roleLabel(normalizedForcedRole)}`;

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isDeletedFilter, setIsDeletedFilter] = useState("false");
  const [bucketFilter, setBucketFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // ✅ Role tabs state
  const [roleTab, setRoleTab] = useState(normalizedForcedRole || "all");

  // Dashboard modal
  const [assignGroupModalOpen, setAssignGroupModalOpen] = useState(false);
  const [usersUploading, setUsersUploading] = useState(false);
  const userUploadInputRef = React.useRef(null);
  const [createAnchorEl, setCreateAnchorEl] = useState(null);
  const [roleModalRole, setRoleModalRole] = useState("");
  const [assignDeviceDialogUser, setAssignDeviceDialogUser] = useState(null);
  const [notificationDialogUser, setNotificationDialogUser] = useState(null);
  const [assignDeviceSearch, setAssignDeviceSearch] = useState("");
  const [assignDeviceFilter, setAssignDeviceFilter] = useState("all");
  const [assignDeviceRowsPerPage, setAssignDeviceRowsPerPage] = useState(20);
  const [assignDevicePage, setAssignDevicePage] = useState(0);
  const [assignOverrides, setAssignOverrides] = useState(() => new Map());
  const [assignDeviceCache, setAssignDeviceCache] = useState(() => new Map());
  const [pendingAssignIds, setPendingAssignIds] = useState(() => new Set());
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [loadingPasswordIds, setLoadingPasswordIds] = useState(() => new Set());

  // Sorting
  const [orderBy, setOrderBy] = useState("updatedAt");
  const [order, setOrder] = useState("desc");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const userActions = getUserActionsEffective(currentUser);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [impersonateUser] = useImpersonateUserMutation();
  const canShowCreateUserButton = useMemo(
    () => [ROLES.SUPER_ADMIN, ROLES.MANAGER, ROLES.HR].includes(currentUserRole),
    [currentUserRole]
  );
  const createMenuOpen = Boolean(createAnchorEl);

  const openAssignDeviceModal = (user) => {
    setAssignToUserId?.(user?._id || "");
    setUserSearchQuery?.(
      user ? `${user.username || user.name || "User"} (${user.mobileNo || user.email || "-"})` : ""
    );
    setAssignDeviceDialogUser(user || null);
    setAssignDeviceSearch("");
    setAssignDeviceFilter("all");
    setAssignDeviceRowsPerPage(20);
    setAssignDevicePage(0);
    setAssignOverrides(new Map());
  };

  const openAssignGroupModal = (user) => {
    setAssignUserToGroupAdminUserId?.(user?._id || "");
    setAssignUserSearchQuery?.(user?.username || user?.name || "");
    setAssignGroupModalOpen(true);
  };

  const resetAssignDeviceDialog = () => {
    setAssignDeviceDialogUser(null);
    setAssignDeviceSearch("");
    setAssignDeviceFilter("all");
    setAssignDevicePage(0);
    setAssignOverrides(new Map());
    setAssignDeviceCache(new Map());
    setPendingAssignIds(new Set());
  };

  // keep pagination reset
  useEffect(() => setPage(0), [searchQuery, roleFilter, statusFilter, isDeletedFilter, roleTab, bucketFilter, fromDate, toDate]);

  // sync roleFilter with roleTab (tabs control main)
  useEffect(() => {
    setRoleFilter(roleTab);
  }, [roleTab]);

  useEffect(() => {
    if (!isRoleScopedPage) return;
    setRoleTab(normalizedForcedRole || "all");
  }, [isRoleScopedPage, normalizedForcedRole]);

const handleLoginAsUser = async (targetUserId) => {
  if (!userActions.canLoginAsUser) {
    alert("You don't have permission to login as other users.");
    return;
  }

  if (
    !window.confirm(
      "Are you sure you want to log in as this user? You will be logged out of Admin."
    )
  ) {
    return;
  }

  try {
    const currentAdminToken = localStorage.getItem("token");
    if (currentAdminToken) localStorage.setItem("adminFallbackToken", currentAdminToken);

    const response = await impersonateUser(targetUserId).unwrap();

    // ✅ Clear RTK Query cache
    dispatch(adminApi.util.resetApiState());

    // ✅ (Optional) Clear whole redux app state if you implemented app/reset
    // dispatch({ type: "app/reset" });

    // ✅ Set new auth
    dispatch(loginSuccess({ user: response.user, token: response.token }));

    // ✅ IMPORTANT: go to USER portal route (change /dashboard to your user portal path)
    navigate("/dashboard", { replace: true, state: { __impersonate: true, t: Date.now() } });
  } catch (error) {
    alert(error?.data?.msg || "Failed to login as user.");
  }
};

  const onDownloadUsersTemplate = async () => {
    try {
      await downloadEntityTemplate("users");
    } catch (error) {
      alert(error?.response?.data?.message || "Users template download failed.");
    }
  };

  const onUsersFileSelected = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    setUsersUploading(true);
    try {
      const result = await uploadEntityFile("users", file, { validateOnly: false });
      alert(result?.message || "Users upload completed.");
    } catch (error) {
      const payload = error?.response?.data;
      const details = Array.isArray(payload?.errors) ? payload.errors.slice(0, 5).join("\n") : "";
      alert(`${payload?.message || "Users upload failed."}${details ? `\n${details}` : ""}`);
    } finally {
      if (userUploadInputRef.current) userUploadInputRef.current.value = "";
      setUsersUploading(false);
    }
  };

  // ✅ filtering
  const filteredUsers = useMemo(() => {
    return (users || [])
      .filter((user) => normalizeRole(user.role) !== ROLES.SUPER_ADMIN)
      .filter((user) => {
        const query = searchQuery.toLowerCase();
        return (
          user.username?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query) ||
          String(user.mobileNo || "").includes(query)
        );
      })
      .filter((user) => roleFilter === "all" || normalizeRole(user.role) === roleFilter)
      .filter((user) => {
        if (statusFilter === "all") return true;
        const isActive = user.isActive !== false && user.isDeleted !== true;
        return statusFilter === "active" ? isActive : !isActive;
      })
      .filter((user) => {
        if (bucketFilter === "all") return true;
        return resolveUserBucket(user) === bucketFilter;
      })
      .filter((user) => {
        const createdAt = user?.createdAt ? new Date(user.createdAt) : null;
        if (!createdAt || Number.isNaN(createdAt.getTime())) return !fromDate && !toDate;
        if (fromDate && createdAt < new Date(`${fromDate}T00:00:00`)) return false;
        if (toDate && createdAt > new Date(`${toDate}T23:59:59`)) return false;
        return true;
      })
      .filter((user) => {
        if (isDeletedFilter === "all") return true;
        const isDeleted = user.isDeleted === true;
        return isDeletedFilter === "true" ? isDeleted : !isDeleted;
      })
      .filter((user) => {
        const highLevelStaff = [
          ROLES.SUPER_ADMIN,
          ROLES.MANAGER,
          ROLES.OFFICE_EMPLOYEE,
          ROLES.HR,
          ROLES.SUB_ADMIN,
        ];

        if (highLevelStaff.includes(currentUserRole)) return true;

        if (currentUserRole === ROLES.GROUP_ADMIN) {
          const userId = normalizeEntityId(user?._id);
          const actorId = normalizeEntityId(currentUserId);
          return (
            userId === actorId ||
            normalizeEntityId(user?.dealerOwner) === actorId ||
            normalizeEntityId(user?.createdBy) === actorId ||
            currentUser?.managedUsers?.some((managedUser) =>
              normalizeEntityId(managedUser) === userId
            )
          );
        }

        const userId = normalizeEntityId(user?._id);
        const actorId = normalizeEntityId(currentUserId);
        return (
          userId === actorId ||
          normalizeEntityId(user?.createdBy) === actorId ||
          currentUser?.managedUsers?.some((managedUser) =>
            normalizeEntityId(managedUser) === userId
          )
        );
      });
  }, [
    users,
    searchQuery,
    roleFilter,
    statusFilter,
    bucketFilter,
    fromDate,
    toDate,
    isDeletedFilter,
    currentUserRole,
    currentUserId,
    currentUser,
  ]);

  // ✅ counts per role for badges (based on current "search/status/deleted" filters except role)
  const roleCounts = useMemo(() => {
    const base = (users || [])
      .filter((u) => u.role !== ROLES.SUPER_ADMIN)
      .filter((u) => {
        const query = searchQuery.toLowerCase();
        return (
          u.username?.toLowerCase().includes(query) ||
          u.email?.toLowerCase().includes(query) ||
          String(u.mobileNo || "").includes(query)
        );
      })
      .filter((u) => {
        if (statusFilter === "all") return true;
        const isActive = u.isActive !== false && u.isDeleted !== true;
        return statusFilter === "active" ? isActive : !isActive;
      })
      .filter((u) => {
        if (isDeletedFilter === "all") return true;
        const isDeleted = u.isDeleted === true;
        return isDeletedFilter === "true" ? isDeleted : !isDeleted;
      })
      .filter((u) => {
        const highLevelStaff = [
          ROLES.SUPER_ADMIN,
          ROLES.MANAGER,
          ROLES.OFFICE_EMPLOYEE,
          ROLES.HR,
          ROLES.SUB_ADMIN,
        ];

        if (highLevelStaff.includes(currentUserRole)) return true;

        if (currentUserRole === ROLES.GROUP_ADMIN) {
          const userId = normalizeEntityId(u?._id);
          const actorId = normalizeEntityId(currentUserId);
          return (
            userId === actorId ||
            normalizeEntityId(u?.dealerOwner) === actorId ||
            normalizeEntityId(u?.createdBy) === actorId ||
            currentUser?.managedUsers?.some((managedUser) =>
              normalizeEntityId(managedUser) === userId
            )
          );
        }

        const userId = normalizeEntityId(u?._id);
        const actorId = normalizeEntityId(currentUserId);
        return (
          userId === actorId ||
          normalizeEntityId(u?.createdBy) === actorId ||
          currentUser?.managedUsers?.some((managedUser) =>
            normalizeEntityId(managedUser) === userId
          )
        );
      });

    const counts = {};
    counts.all = base.length;
    (manageableRoles || []).forEach((r) => {
      counts[r] = base.filter((u) => normalizeRole(u.role) === r).length;
    });
    return counts;
  }, [
    users,
    manageableRoles,
    searchQuery,
    statusFilter,
    isDeletedFilter,
    currentUserRole,
    currentUserId,
    currentUser,
  ]);

  // Sorting
  const sortedUsers = useMemo(() => {
    const arr = [...filteredUsers];

    const getVal = (u, key) => {
      if (key === "department") {
        return String(resolveUserBucket(u) || "").toLowerCase();
      }
      const raw = unwrapMongoValue(u?.[key]);

      if (isProbablyDate(raw, key)) {
        const t = new Date(raw).getTime();
        return Number.isFinite(t) ? t : 0;
      }
      if (typeof raw === "boolean") return raw ? 1 : 0;
      if (typeof raw === "number") return raw;
      if (typeof raw === "string") return raw.toLowerCase();
      if (Array.isArray(raw)) return raw.length;
      if (raw && typeof raw === "object") return JSON.stringify(raw);
      return raw ?? "";
    };

    arr.sort((a, b) => {
      const av = getVal(a, orderBy);
      const bv = getVal(b, orderBy);
      if (av < bv) return order === "asc" ? -1 : 1;
      if (av > bv) return order === "asc" ? 1 : -1;
      return 0;
    });

    return arr;
  }, [filteredUsers, orderBy, order]);

  const onRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const roleTabs = useMemo(() => {
    const list = ["all", ...(manageableRoles || [])];
    // avoid duplicates
    return Array.from(new Set(list));
  }, [manageableRoles]);

  const accessibleUsers = useMemo(
    () => (Array.isArray(users) ? users : []).filter((user) => normalizeRole(user.role) !== ROLES.SUPER_ADMIN),
    [users]
  );

  const analyticsUsers = useMemo(() => {
    if (!isRoleScopedPage) return accessibleUsers;
    return accessibleUsers.filter((user) => normalizeRole(user.role) === normalizedForcedRole);
  }, [accessibleUsers, isRoleScopedPage, normalizedForcedRole]);

  const roleBucketData = useMemo(() => {
    const grouped = analyticsUsers.reduce((acc, user) => {
      const key = resolveUserBucket(user) || "Others";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(grouped)
      .map(([name, value], index) => ({
        name,
        value,
        color: ANALYTICS_COLORS[index % ANALYTICS_COLORS.length],
      }))
      .filter((item) => item.value > 0);
  }, [analyticsUsers]);

  useEffect(() => {
    const userId = String(assignDeviceDialogUser?._id || "");
    if (!userId) {
      setAssignDeviceCache(new Map());
      return;
    }

    setAssignDeviceCache(() => {
      const next = new Map();
      (Array.isArray(safeDevices) ? safeDevices : []).forEach((device) => {
        const deviceId = String(device?._id || device?.id || "");
        if (!deviceId) return;
        next.set(deviceId, { ...device });
      });
      return next;
    });
  }, [assignDeviceDialogUser?._id, safeDevices]);

  const usersByRole = useMemo(() => {
    const counts = {};
    ANALYTICS_ROLE_ORDER.forEach((role) => {
      counts[role] = accessibleUsers.filter((user) => normalizeRole(user.role) === role).length;
    });
    return counts;
  }, [accessibleUsers]);

  const analyticsCards = useMemo(() => {
    const activeUsers = analyticsUsers.filter((user) => user.isActive !== false && !user.isDeleted).length;
    const inactiveUsers = analyticsUsers.filter((user) => user.isActive === false || user.isDeleted).length;
    const adminUsers = analyticsUsers.filter((user) => normalizeRole(user.role) === ROLES.SUB_ADMIN).length;
    const now = new Date();
    const newUsersThisMonth = analyticsUsers.filter((user) => {
      const createdAt = user?.createdAt ? new Date(user.createdAt) : null;
      return createdAt && !Number.isNaN(createdAt.getTime()) && createdAt.getMonth() === now.getMonth() && createdAt.getFullYear() === now.getFullYear();
    }).length;

    if (isRoleScopedPage) {
      return [
        {
          key: "total",
          label: `Total ${entityPluralLabel}`,
          value: analyticsUsers.length,
          caption: "All time",
          color: "#2563eb",
          icon: <TotalUsersStatIcon />,
          sparkline: buildSparklineSeries(Math.max(analyticsUsers.length, 4)),
        },
        {
          key: "active",
          label: `Active ${entityPluralLabel}`,
          value: activeUsers,
          caption: `${analyticsUsers.length ? ((activeUsers / Math.max(analyticsUsers.length, 1)) * 100).toFixed(1) : "0.0"}% of total`,
          color: "#16a34a",
          icon: <ActiveUsersStatIcon />,
          sparkline: buildSparklineSeries(Math.max(activeUsers, 3)),
        },
        {
          key: "inactive",
          label: `Inactive ${entityPluralLabel}`,
          value: inactiveUsers,
          caption: `${analyticsUsers.length ? ((inactiveUsers / Math.max(analyticsUsers.length, 1)) * 100).toFixed(1) : "0.0"}% of total`,
          color: "#f59e0b",
          icon: <InactiveUsersStatIcon />,
          sparkline: buildSparklineSeries(Math.max(inactiveUsers, 2)),
        },
        {
          key: "new",
          label: `New ${entityPluralLabel} (This Month)`,
          value: newUsersThisMonth,
          caption: "Added this month",
          color: "#06b6d4",
          icon: <NewUsersStatIcon />,
          sparkline: buildSparklineSeries(Math.max(newUsersThisMonth, 2)),
        },
      ];
    }

    return [
      {
        key: "total",
        label: "Total Users",
        value: accessibleUsers.length,
        caption: `${Object.values(usersByRole).filter(Boolean).length} active role buckets`,
        color: "#2563eb",
        icon: <TotalUsersStatIcon />,
        sparkline: buildSparklineSeries(Math.max(accessibleUsers.length, 4)),
      },
      {
        key: "active",
        label: "Active Users",
        value: activeUsers,
        caption: `${analyticsUsers.length ? ((activeUsers / analyticsUsers.length) * 100).toFixed(1) : "0.0"}% of total`,
        color: "#16a34a",
        icon: <ActiveUsersStatIcon />,
        sparkline: buildSparklineSeries(Math.max(activeUsers, 3)),
      },
      {
        key: "inactive",
        label: "Inactive Users",
        value: inactiveUsers,
        caption: `${analyticsUsers.length ? ((inactiveUsers / analyticsUsers.length) * 100).toFixed(1) : "0.0"}% of total`,
        color: "#f59e0b",
        icon: <InactiveUsersStatIcon />,
        sparkline: buildSparklineSeries(Math.max(inactiveUsers, 2)),
      },
      {
        key: "admins",
        label: "Admins",
        value: adminUsers,
        caption: `${analyticsUsers.length ? ((adminUsers / analyticsUsers.length) * 100).toFixed(1) : "0.0"}% of total`,
        color: "#7c3aed",
        icon: <AdminUsersStatIcon />,
        sparkline: buildSparklineSeries(Math.max(adminUsers, 2)),
      },
      {
        key: "new",
        label: "New Users (This Month)",
        value: newUsersThisMonth,
        caption: `${newUsersThisMonth} added this month`,
        color: "#06b6d4",
        icon: <NewUsersStatIcon />,
        sparkline: buildSparklineSeries(Math.max(newUsersThisMonth, 2)),
      },
    ];
  }, [accessibleUsers, analyticsUsers, entityPluralLabel, isRoleScopedPage, usersByRole]);

  const roleChartData = useMemo(
    () =>
      ANALYTICS_ROLE_ORDER.map((role) => {
        const meta = getRoleMeta(role);
        return {
          name: meta.title,
          value: usersByRole[role] || 0,
          color: meta.tone,
          role,
        };
      }).filter((item) => item.value > 0),
    [usersByRole]
  );

  const statusChartData = useMemo(() => {
    const active = analyticsUsers.filter((user) => user.isActive !== false && !user.isDeleted).length;
    const inactive = analyticsUsers.length - active;
    return [
      { name: "Active", value: active, color: "#22c55e" },
      { name: "Inactive", value: inactive, color: "#ef4444" },
    ];
  }, [analyticsUsers]);

  const registrationTrendData = useMemo(() => {
    const now = new Date();
    const totalDays = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return Array.from({ length: totalDays }, (_, index) => {
      const day = index + 1;
      const count = analyticsUsers.filter((user) => {
        const createdAt = user?.createdAt ? new Date(user.createdAt) : null;
        return (
          createdAt &&
          !Number.isNaN(createdAt.getTime()) &&
          createdAt.getFullYear() === now.getFullYear() &&
          createdAt.getMonth() === now.getMonth() &&
          createdAt.getDate() <= day
        );
      }).length;
      return {
        label: `${String(day).padStart(2, "0")} ${now.toLocaleString("en-GB", { month: "short" })}`,
        value: count,
      };
    });
  }, [analyticsUsers]);

  const filterCount = [
    statusFilter !== "all",
    isDeletedFilter !== "false",
    !isRoleScopedPage && roleTab !== "all",
    bucketFilter !== "all",
    Boolean(fromDate),
    Boolean(toDate),
  ].filter(Boolean).length;

  const allAssignDeviceRows = useMemo(() => {
    const userId = String(assignDeviceDialogUser?._id || "");
    const normalizedTargetRole = normalizeRole(assignDeviceDialogUser?.role);
    const usesSharedAssignments = [ROLES.DRIVER, ROLES.CLIENT, ROLES.VIEWER, ROLES.USER].includes(normalizedTargetRole);
    if (!userId) return [];

    return Array.from(assignDeviceCache.values()).map((device) => {
      const deviceId = String(device?._id || device?.id || "");
      const assignedUserId = String(device?.assignedToUser?._id || device?.assignedToUser || "");
      const assignedViewers = Array.isArray(device?.assignedToViewers) ? device.assignedToViewers : [];
      const assignedViewerIds = assignedViewers.map((viewer) => String(viewer?._id || viewer));
      const isPrimaryAssigned = usesSharedAssignments ? isDeviceOwnedByUser(device, userId) : assignedUserId === userId;
      const isSharedAssigned = assignedViewerIds.includes(userId);
      const initiallyAssigned = usesSharedAssignments ? (isPrimaryAssigned || isSharedAssigned) : isPrimaryAssigned;
      const overrideChecked = assignOverrides.get(deviceId);
      const checked = typeof overrideChecked === "boolean" ? overrideChecked : initiallyAssigned;
      const statusMeta = getStatusMeta(device?.liveData?.vehicleStatus || device?.status);
      return {
        id: deviceId,
        checked,
        initiallyAssigned,
        assignedToUserId: assignedUserId,
        assignedToViewers: assignedViewerIds,
        adminOwnerId: String(device?.adminId?._id || device?.adminId || ""),
        dealerOwnerId: String(device?.dealer?._id || device?.dealer || device?.dealerOwner?._id || device?.dealerOwner || ""),
        name: resolveDeviceVehicleLabel(device),
        imei: device?.imei || device?.IMEI || "-",
        statusKey: statusMeta.key,
        statusLabel: statusMeta.label,
        statusColor: statusMeta.color,
        statusBg: statusMeta.bg,
        lastUpdate: formatDateTimeCompact(resolveDeviceLastUpdate(device)),
        simNo: resolveDeviceSimNo(device),
        model: device?.model || "-",
        iconLabel: device?.vehicleType || device?.deviceIcon || "Vehicle",
      };
    });
  }, [assignDeviceDialogUser?._id, assignDeviceDialogUser?.role, assignDeviceCache, assignOverrides]);

  const assignDeviceRows = useMemo(() => {
    const searchValue = String(assignDeviceSearch || "").trim().toLowerCase();
    if (!allAssignDeviceRows.length) return [];

    const filtered = allAssignDeviceRows.filter((row) => {
      const matchesSearch =
        !searchValue ||
        [row.name, row.imei, row.simNo, row.model].some((value) =>
          String(value || "").toLowerCase().includes(searchValue)
        );

      if (!matchesSearch) return false;

      if (assignDeviceFilter === "assigned") return row.checked;
      if (assignDeviceFilter === "unassigned") return !row.checked;
      if (["running", "stopped", "no_reporting"].includes(assignDeviceFilter)) {
        return row.statusKey === assignDeviceFilter;
      }
      return true;
    });

    filtered.sort((left, right) => {
      if (left.checked !== right.checked) return Number(right.checked) - Number(left.checked);
      return String(left.name || "").localeCompare(String(right.name || ""));
    });

    return filtered;
  }, [allAssignDeviceRows, assignDeviceFilter, assignDeviceSearch]);

  const assignDevicePagedRows = useMemo(
    () =>
      assignDeviceRows.slice(
        assignDevicePage * assignDeviceRowsPerPage,
        assignDevicePage * assignDeviceRowsPerPage + assignDeviceRowsPerPage
      ),
    [assignDevicePage, assignDeviceRows, assignDeviceRowsPerPage]
  );

  const assignDeviceStats = useMemo(() => {
    const total = allAssignDeviceRows.length;
    const assigned = allAssignDeviceRows.filter((row) => row.checked).length;
    const unassigned = total - assigned;
    const selectedNow = allAssignDeviceRows.filter((row) => row.checked !== row.initiallyAssigned).length;
    return { total, assigned, unassigned, selectedNow };
  }, [allAssignDeviceRows]);

  const visibleDeviceIds = useMemo(() => assignDevicePagedRows.map((row) => row.id), [assignDevicePagedRows]);

  const persistAssignDeviceChange = async (row, nextChecked, options = {}) => {
    const userId = String(assignDeviceDialogUser?._id || "");
    const normalizedTargetRole = normalizeRole(assignDeviceDialogUser?.role);
    const usesSharedAssignments = [ROLES.DRIVER, ROLES.CLIENT, ROLES.VIEWER, ROLES.USER].includes(normalizedTargetRole);
    const fallbackOwnerId = String(currentUser?._id || currentUser?.id || "");
    const deviceId = String(row?.id || "");
    if (!userId || !deviceId) return false;

    setPendingAssignIds((prev) => new Set(prev).add(deviceId));
    try {
      if (usesSharedAssignments) {
        const isPrimaryAssigned = String(row.assignedToUserId || "") === userId;
        const nextAssignedViewers = nextChecked
          ? Array.from(new Set([...(row.assignedToViewers || []), userId]))
          : (row.assignedToViewers || []).filter((id) => String(id) !== userId);

        const payload = {
          deviceId,
          assignedToViewers: nextAssignedViewers,
        };

        if (isPrimaryAssigned && !nextChecked) {
          payload.assignedToUser = null;
        }
        if (!nextChecked && String(row.adminOwnerId || "") === userId && fallbackOwnerId) {
          payload.adminId = fallbackOwnerId;
        }
        if (!nextChecked && String(row.dealerOwnerId || "") === userId) {
          payload.dealer = null;
        }

        await editDevice(payload).unwrap();

        setAssignDeviceCache((prev) => {
          const next = new Map(prev);
          const existing = next.get(deviceId) || {};
          next.set(deviceId, {
            ...existing,
            _id: existing?._id || deviceId,
            assignedToUser: isPrimaryAssigned && !nextChecked ? null : (existing?.assignedToUser ?? row.assignedToUserId ?? null),
            adminId:
              !nextChecked && String(row.adminOwnerId || "") === userId && fallbackOwnerId
                ? fallbackOwnerId
                : (existing?.adminId ?? row.adminOwnerId ?? null),
            dealer:
              !nextChecked && String(row.dealerOwnerId || "") === userId
                ? null
                : (existing?.dealer ?? row.dealerOwnerId ?? null),
            assignedToViewers: nextAssignedViewers,
          });
          return next;
        });
      } else {
        const payload = {
          deviceId,
          assignedToUser: nextChecked ? userId : null,
        };

        if (!nextChecked) {
          if (String(row.adminOwnerId || "") === userId && fallbackOwnerId) {
            payload.adminId = fallbackOwnerId;
          }
          if (String(row.dealerOwnerId || "") === userId) {
            payload.dealer = null;
          }
        }

        await editDevice(payload).unwrap();

        setAssignDeviceCache((prev) => {
          const next = new Map(prev);
          const existing = next.get(deviceId) || {};
          next.set(deviceId, {
            ...existing,
            _id: existing?._id || deviceId,
            assignedToUser: nextChecked ? userId : null,
            adminId:
              !nextChecked && String(row.adminOwnerId || "") === userId && fallbackOwnerId
                ? fallbackOwnerId
                : (existing?.adminId ?? row.adminOwnerId ?? null),
            dealer:
              !nextChecked && String(row.dealerOwnerId || "") === userId
                ? null
                : (existing?.dealer ?? row.dealerOwnerId ?? null),
          });
          return next;
        });
      }

      setAssignOverrides((prev) => {
        const next = new Map(prev);
        next.delete(deviceId);
        return next;
      });
      await onRefreshData?.();
      if (!options.silent) {
        showStatus("success", nextChecked ? "Device assigned successfully." : "Device unassigned successfully.");
      }
      return true;
    } catch (error) {
      if (!options.silent) {
        showStatus("error", error?.data?.msg || "Failed to update device assignment.");
      }
      return false;
    } finally {
      setPendingAssignIds((prev) => {
        const next = new Set(prev);
        next.delete(deviceId);
        return next;
      });
    }
  };

  const handleToggleAssignDevice = async (row, nextChecked) => {
    if (!row) return;
    await persistAssignDeviceChange(row, nextChecked);
  };

  const handleSelectAllVisible = async () => {
    const rowsToAssign = assignDevicePagedRows.filter((row) => !row.checked);
    if (!rowsToAssign.length) {
      showStatus("info", "All visible devices are already assigned.");
      return;
    }

    let updated = 0;
    for (const row of rowsToAssign) {
      const ok = await persistAssignDeviceChange(row, true, { silent: true });
      if (ok) updated += 1;
    }
    showStatus(updated ? "success" : "error", updated ? `${updated} visible devices assigned.` : "Failed to assign visible devices.");
  };

  const handleClearVisible = async () => {
    const rowsToUnassign = assignDevicePagedRows.filter((row) => row.checked);
    if (!rowsToUnassign.length) {
      showStatus("info", "No visible assigned devices to unassign.");
      return;
    }

    let updated = 0;
    for (const row of rowsToUnassign) {
      const ok = await persistAssignDeviceChange(row, false, { silent: true });
      if (ok) updated += 1;
    }
    showStatus(updated ? "success" : "error", updated ? `${updated} visible devices unassigned.` : "Failed to unassign visible devices.");
  };

  useEffect(() => {
    setAssignDevicePage(0);
  }, [assignDeviceSearch, assignDeviceFilter, assignDeviceRowsPerPage]);

  const canRevealPasswords =
    currentUserRole === ROLES.SUPER_ADMIN ||
    currentUserRole === ROLES.SUB_ADMIN ||
    currentUserRole === ROLES.DEALER;

  const handleToggleDisabled = async (user, checked) => {
    if (!user?._id) return;
    try {
      await editUser({
        userId: user._id,
        isActive: !checked,
      }).unwrap();
      await onRefreshData?.();
      showStatus("success", "User status updated.");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to update account status.");
    }
  };

  const handleTogglePassword = async (userId) => {
    const targetId = String(userId || "");
    if (!targetId || !canRevealPasswords) return;

    if (Object.prototype.hasOwnProperty.call(revealedPasswords, targetId)) {
      setRevealedPasswords((prev) => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
      return;
    }

    setLoadingPasswordIds((prev) => new Set(prev).add(targetId));
    try {
      const response = await revealUserPassword(targetId).unwrap();
      setRevealedPasswords((prev) => ({
        ...prev,
        [targetId]: response?.password || "",
      }));
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to reveal password.");
    } finally {
      setLoadingPasswordIds((prev) => {
        const next = new Set(prev);
        next.delete(targetId);
        return next;
      });
    }
  };

  const renderPasswordCell = (user) => {
    const userId = String(user?._id || "");
    const loading = loadingPasswordIds.has(userId);
    const revealedPassword = revealedPasswords[userId];
    const isVisible = Object.prototype.hasOwnProperty.call(revealedPasswords, userId);

    return (
      <Stack direction="row" spacing={0.8} alignItems="center">
        <Typography sx={{ fontWeight: 700, color: "#334155", minWidth: 86 }}>
          {isVisible ? revealedPassword || "—" : "••••••"}
        </Typography>
        {canRevealPasswords ? (
          <Tooltip title={isVisible ? "Hide password" : "Show password"}>
            <IconButton size="small" onClick={() => handleTogglePassword(userId)} sx={actionButtonSx("info")}>
              {loading ? (
                <CircularProgress size={16} />
              ) : isVisible ? (
                <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
              ) : (
                <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        p: { xs: 1.1, md: 2.25 },
        display: "flex",
        flexDirection: "column",
        gap: 2.2,
        minHeight: "100%",
        background:
          "radial-gradient(circle at top right, rgba(37,99,235,0.08), transparent 22%), linear-gradient(180deg, #f5f7fb 0%, #eef4fb 100%)",
      }}
    >
      <Stack
        direction={{ xs: "column", xl: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", xl: "center" }}
        justifyContent="space-between"
      >
        <Box>
          {pageBreadcrumb ? (
            <Typography sx={{ color: "#7c8ba1", fontSize: 13.5, mb: 0.6 }}>
              {pageBreadcrumb}
            </Typography>
          ) : null}
          <Typography sx={{ fontWeight: 800, fontSize: { xs: 28, md: 34 }, color: "#0f172a", letterSpacing: "-0.03em" }}>
            {roleScopedTitle}
          </Typography>
          <Typography sx={{ mt: 0.7, color: "text.secondary", fontSize: 16 }}>
            {roleScopedSubtitle}
          </Typography>
        </Box>
        {isRoleScopedPage ? (
          <Button
            variant="contained"
            onClick={() => setRoleModalRole(directCreateRole || normalizedForcedRole)}
            sx={{
              alignSelf: { xs: "stretch", md: "flex-start" },
              minHeight: 52,
              borderRadius: 3,
              px: 2.4,
              fontWeight: 800,
              textTransform: "none",
              boxShadow: "0 18px 34px rgba(37,99,235,0.24)",
            }}
          >
            + {roleScopedAddLabel}
          </Button>
        ) : (
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.2}
            sx={{ width: { xs: "100%", xl: "auto" }, alignItems: "stretch" }}
          >
            <TextField
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, login id or phone..."
              sx={{
                minWidth: { xs: "100%", md: 360 },
                "& .MuiOutlinedInput-root": {
                  height: 56,
                  borderRadius: 3,
                  bgcolor: "#fff",
                  boxShadow: "0 12px 32px rgba(15, 23, 42, 0.06)",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Chip size="small" label="⌘ K" sx={{ fontWeight: 800, bgcolor: alpha("#0f172a", 0.05) }} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        )}
      </Stack>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            lg: "repeat(5, minmax(0, 1fr))",
          },
        }}
      >
        {analyticsCards.map((card) => (
          <AnalyticsStatCard key={card.key} {...card} />
        ))}
      </Box>

      <Grid container spacing={2} sx={{ order: 3 }}>
        <Grid item xs={12} lg={4}>
          <FloatingCard
            title={isRoleScopedPage ? `${entityPluralLabel} by Status` : "Users by Role"}
            action={
              <Chip
                label="All Time"
                sx={{ height: 34, borderRadius: 2.5, bgcolor: alpha("#2563eb", 0.08), color: "#2563eb", fontWeight: 700 }}
              />
            }
            height="100%"
          >
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center">
              <Box sx={{ width: { xs: "100%", md: 220 }, height: { xs: 220, lg: 200 } }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={isRoleScopedPage ? statusChartData : roleChartData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={66}
                      outerRadius={98}
                      paddingAngle={3}
                      stroke="none"
                    >
                      {(isRoleScopedPage ? statusChartData : roleChartData).map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Stack spacing={1.2} sx={{ flex: 1, width: "100%" }}>
                <Box sx={{ textAlign: { xs: "left", md: "center" } }}>
                  <Typography sx={{ fontSize: 34, fontWeight: 800, color: "#0f172a", lineHeight: 1.05 }}>
                    {analyticsUsers.length}
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary" }}>
                    Total {entityPluralLabel}
                  </Typography>
                </Box>
                {(isRoleScopedPage ? statusChartData : roleChartData).map((item) => (
                  <Stack key={item.role || item.name} direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: item.color }} />
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>{item.name}</Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#475569" }}>
                      {item.value} ({analyticsUsers.length ? ((item.value / analyticsUsers.length) * 100).toFixed(1) : "0.0"}%)
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </FloatingCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <FloatingCard
            title={isRoleScopedPage ? `${entityPluralLabel} Added Over Time` : "User Registration Trend"}
            action={
              <Chip
                label="This Month"
                sx={{ height: 34, borderRadius: 2.5, bgcolor: alpha("#2563eb", 0.08), color: "#2563eb", fontWeight: 700 }}
              />
            }
            height="100%"
          >
            <Box sx={{ width: "100%", height: { xs: 240, lg: 210 } }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={registrationTrendData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={alpha("#94a3b8", 0.24)} />
                  <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 12 }} interval="preserveStartEnd" tickLine={false} axisLine={false} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 12 }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <RechartsTooltip />
                  <Line type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={3} dot={{ r: 2.5, fill: "#2563eb" }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </FloatingCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <FloatingCard
            title={isRoleScopedPage ? `${entityPluralLabel} by ${bucketLabel}` : "Users by Status"}
            action={
              <Chip
                label="All Time"
                sx={{ height: 34, borderRadius: 2.5, bgcolor: alpha("#2563eb", 0.08), color: "#2563eb", fontWeight: 700 }}
              />
            }
            height="100%"
          >
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center">
              <Box sx={{ width: { xs: "100%", md: 220 }, height: { xs: 220, lg: 200 } }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={isRoleScopedPage ? roleBucketData : statusChartData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={66}
                      outerRadius={98}
                      paddingAngle={4}
                      stroke="none"
                    >
                      {(isRoleScopedPage ? roleBucketData : statusChartData).map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Stack spacing={2} sx={{ flex: 1, width: "100%" }}>
                <Box sx={{ textAlign: { xs: "left", md: "center" } }}>
                  <Typography sx={{ fontSize: 34, fontWeight: 800, color: "#0f172a", lineHeight: 1.05 }}>
                    {analyticsUsers.length}
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary" }}>
                    Total {entityPluralLabel}
                  </Typography>
                </Box>
                {(isRoleScopedPage ? roleBucketData : statusChartData).map((item) => (
                  <Stack key={item.name} direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: item.color }} />
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>{item.name}</Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#475569" }}>
                      {item.value} ({analyticsUsers.length ? ((item.value / analyticsUsers.length) * 100).toFixed(1) : "0.0"}%)
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </FloatingCard>
        </Grid>
      </Grid>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.6, md: 2.1 },
          borderRadius: 4,
          borderColor: alpha("#0f172a", 0.08),
          boxShadow: "0 18px 44px rgba(15, 23, 42, 0.05)",
          background: "#fff",
        }}
      >
        <Stack spacing={1.8}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} justifyContent="space-between">
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
              <Button
                variant="outlined"
                startIcon={<Download size={16} />}
                onClick={onDownloadUsersTemplate}
                sx={{ borderRadius: 2.5, px: 2, fontWeight: 800, textTransform: "none" }}
              >
                Export
              </Button>
              <Button
                variant="outlined"
                startIcon={<Upload size={16} />}
                onClick={() => userUploadInputRef.current?.click()}
                disabled={usersUploading}
                sx={{ borderRadius: 2.5, px: 2, fontWeight: 800, textTransform: "none" }}
              >
                {usersUploading ? "Importing..." : "Import Users"}
              </Button>
              {userActions.canCreate && canShowCreateUserButton && setShowCreateUserModal && (
                <Button
                  variant="contained"
                  onClick={(event) => {
                    if (isRoleScopedPage) {
                      setRoleModalRole(directCreateRole || normalizedForcedRole);
                      return;
                    }
                    setCreateAnchorEl(event.currentTarget);
                  }}
                  sx={{ borderRadius: 2.5, px: 2.2, fontWeight: 800, textTransform: "none", boxShadow: "none" }}
                >
                  + {isRoleScopedPage ? roleScopedAddLabel : "Add User"}
                </Button>
              )}
            </Stack>
          </Stack>

          <Stack direction={{ xs: "column", xl: "row" }} spacing={1.2} alignItems={{ xs: "stretch", xl: "center" }} justifyContent="space-between">
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} sx={{ flex: 1 }}>
              {!isRoleScopedPage ? (
                <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 160 } }}>
                  <Select value={roleTab} onChange={(e) => setRoleTab(e.target.value)}>
                    {roleTabs.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role === "all" ? "All Roles" : roleLabel(role)}
                        {typeof roleCounts?.[role] === "number" ? ` (${roleCounts[role]})` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              <TextField
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={isRoleScopedPage ? "Search by name, email or phone..." : "Search by name, login id or phone..."}
                sx={{ flex: 1, minWidth: { md: 320 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 180 } }}>
                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active Only</MenuItem>
                  <MenuItem value="disabled">Disabled Only</MenuItem>
                </Select>
              </FormControl>
              {isRoleScopedPage ? (
                <>
                  <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 190 } }}>
                    <Select value={bucketFilter} onChange={(e) => setBucketFilter(e.target.value)}>
                      <MenuItem value="all">All {bucketLabel}s</MenuItem>
                      {Array.from(new Set(analyticsUsers.map((user) => resolveUserBucket(user)).filter(Boolean))).map((bucket) => (
                        <MenuItem key={bucket} value={bucket}>
                          {bucket}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    type="date"
                    size="small"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    sx={{ minWidth: { xs: "100%", md: 165 } }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    type="date"
                    size="small"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    sx={{ minWidth: { xs: "100%", md: 165 } }}
                    InputLabelProps={{ shrink: true }}
                  />
                </>
              ) : (
                <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 180 } }}>
                  <Select value={isDeletedFilter} onChange={(e) => setIsDeletedFilter(e.target.value)}>
                    <MenuItem value="false">Active Users</MenuItem>
                    <MenuItem value="true">Deleted Users</MenuItem>
                    <MenuItem value="all">All Records</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
              <Button
                variant="outlined"
                startIcon={<FiltersToolbarIcon />}
                sx={{ borderRadius: 2.5, px: 2, fontWeight: 800, textTransform: "none" }}
              >
                Filters
                {filterCount > 0 ? (
                  <Chip
                    size="small"
                    label={filterCount}
                    sx={{ ml: 1, height: 22, borderRadius: 1.5, fontWeight: 800, bgcolor: "#2563eb", color: "#fff" }}
                  />
                ) : null}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshToolbarIcon />}
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter("all");
                  setIsDeletedFilter("false");
                  setRoleTab(normalizedForcedRole || "all");
                  setBucketFilter("all");
                  setFromDate("");
                  setToDate("");
                  onRefreshData?.();
                }}
                sx={{ borderRadius: 2.5, px: 2, fontWeight: 800, textTransform: "none" }}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <input
        ref={userUploadInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: "none" }}
        onChange={onUsersFileSelected}
      />

      {/* Table */}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          borderColor: alpha("#0f172a", 0.08),
          display: "flex",
          flexDirection: "column",
          flex: "0 0 auto",
          minHeight: 0,
          height: "auto",
          boxShadow: "0 18px 44px rgba(15, 23, 42, 0.05)",
        }}
      >
        {sortedUsers.length > 0 ? (
          <>
            {isMdDown ? (
              <Stack spacing={1.25} sx={{ p: 1.25 }}>
                {sortedUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((user) => {
                    const meta = getRoleMeta(user.role);
                    const permissionLabels = collectPermissionLabels(user.permissions);
                    return (
                      <Paper
                        key={unwrapMongoValue(user._id) || user._id}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 3,
                          borderColor: alpha("#0f172a", 0.08),
                          boxShadow: "0 10px 28px rgba(15, 23, 42, 0.04)",
                        }}
                      >
                        <Stack spacing={1.3}>
                          <Stack direction="row" spacing={1.2} alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
                              <Avatar sx={{ width: 42, height: 42, bgcolor: alpha(meta.tone, 0.12), color: meta.tone, fontWeight: 800 }}>
                                {getInitials(user.username)}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 800, color: "#0f172a" }} noWrap>
                                  {user.username || user.name || "-"}
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: "text.secondary" }} noWrap>
                                  {user.email || user.uniqueId || "-"}
                                </Typography>
                              </Box>
                            </Stack>
                            <Chip size="small" label={roleLabel(user.role)} sx={{ bgcolor: alpha(meta.tone, 0.12), color: meta.tone, fontWeight: 800, borderRadius: 2 }} />
                          </Stack>
                          <Grid container spacing={1.1}>
                            <Grid item xs={6}>
                              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Login ID</Typography>
                              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{user.uniqueId || "-"}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Phone</Typography>
                              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{user.mobileNo || "-"}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Email</Typography>
                              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{user.email || "-"}</Typography>
                            </Grid>
                            {isRoleScopedPage ? (
                              <Grid item xs={6}>
                                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{bucketLabel}</Typography>
                                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{resolveUserBucket(user)}</Typography>
                              </Grid>
                            ) : null}
                            <Grid item xs={6}>
                              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Created By</Typography>
                              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{resolveCreatedByLabel(user)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Status</Typography>
                              <Chip
                                size="small"
                                label={user.isDeleted ? "Deleted" : user.isActive === false ? "Inactive" : "Active"}
                                sx={{
                                  mt: 0.4,
                                  bgcolor: user.isDeleted ? alpha("#ef4444", 0.12) : user.isActive === false ? alpha("#f59e0b", 0.12) : alpha("#22c55e", 0.12),
                                  color: user.isDeleted ? "#ef4444" : user.isActive === false ? "#d97706" : "#16a34a",
                                  fontWeight: 800,
                                  borderRadius: 2,
                                }}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Created</Typography>
                              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{formatShortDate(user.createdAt)}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.45 }}>Password</Typography>
                              {renderPasswordCell(user)}
                            </Grid>
                            <Grid item xs={12}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                  px: 1.1,
                                  py: 0.65,
                                  borderRadius: 2,
                                  border: `1px solid ${alpha("#0f172a", 0.08)}`,
                                  bgcolor: alpha("#f8fafc", 0.92),
                                }}
                              >
                                <Box>
                                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Account Access</Typography>
                                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
                                    {user.isDeleted ? "Deleted" : user.isActive === false ? "Disabled" : "Enabled"}
                                  </Typography>
                                </Box>
                                <Switch
                                  checked={user.isActive === false}
                                  onChange={(event) => handleToggleDisabled(user, event.target.checked)}
                                  disabled={
                                    user.isDeleted ||
                                    !userActions.canEdit ||
                                    (String(user._id) === String(currentUserId) && currentUserRole === ROLES.SUPER_ADMIN)
                                  }
                                />
                              </Stack>
                            </Grid>
                          </Grid>
                          {(permissionLabels.length > 0 || user.remainingTokens || user.licenseSummary?.totalRemainingCredits) && (
                            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                              {permissionLabels.slice(0, 2).map((label) => (
                                <Chip key={label} size="small" label={label} variant="outlined" sx={{ borderRadius: 2, fontWeight: 700 }} />
                              ))}
                              {(user.remainingTokens || user.licenseSummary?.totalRemainingCredits) ? (
                                <CreditsCell
                                  credits={user.credits}
                                  licenseSummary={user.licenseSummary}
                                  remainingTokens={user.remainingTokens}
                                />
                              ) : null}
                            </Stack>
                          )}
                          <Stack direction="row" spacing={0.8} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                            {isRoleScopedPage && (
                              <Tooltip title="View">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setEditingUser({
                                      ...user,
                                      isActive: user.isActive ?? true,
                                      devicePermissions: user.devicePermissions || { create: false, read: false, update: false, delete: false, command: false },
                                      userPermissions: user.userPermissions || { create: false, read: false, update: false, delete: false, assign: false },
                                    })
                                  }
                                  sx={actionTileSx("info")}
                                >
                                  <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!user.isDeleted && userActions.canLoginAsUser && (
                              <Tooltip title="Login as User">
                                <IconButton size="small" onClick={() => handleLoginAsUser(user._id)} sx={actionTileSx("slate")}>
                                  <LogIn size={16} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!user.isDeleted && (
                              <Tooltip title="Notification Alerts">
                                <IconButton size="small" onClick={() => setNotificationDialogUser(user)} sx={actionTileSx("warning")}>
                                  <NotificationsActiveOutlinedIcon sx={{ fontSize: 19 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!user.isDeleted && canAssignDevice && (
                              <Tooltip title="Assign Device">
                                <IconButton size="small" onClick={() => openAssignDeviceModal(user)} sx={actionTileSx("primary")}>
                                  <FleetVehicleIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!user.isDeleted && canAssignUserToGA && (
                              <Tooltip title="Link User To Group">
                                <IconButton size="small" onClick={() => openAssignGroupModal(user)} sx={actionTileSx("info")}>
                                  <FleetUserAssignIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {userActions.canEdit && (
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setEditingUser({
                                      ...user,
                                      isActive: user.isActive ?? true,
                                      devicePermissions: user.devicePermissions || { create: false, read: false, update: false, delete: false, command: false },
                                      userPermissions: user.userPermissions || { create: false, read: false, update: false, delete: false, assign: false },
                                    })
                                  }
                                  sx={actionTileSx("primary")}
                                >
                                  <EditIcon size={16} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {userActions.canResetPassword && (
                              <Tooltip title="Reset Password">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setResetUserId(user._id);
                                    setShowResetPasswordModal(true);
                                  }}
                                  sx={actionTileSx("warning")}
                                >
                                  <KeyRound size={16} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {user.isDeleted ? (
                              userActions.canRestore && (
                                <Tooltip title="Restore">
                                  <IconButton size="small" onClick={() => handleRestoreUser(user._id)} sx={actionTileSx("success")}>
                                    <RestoreIcon size={16} />
                                  </IconButton>
                                </Tooltip>
                              )
                            ) : (
                              userActions.canDelete && (
                                <Tooltip title="Delete">
                                  <IconButton size="small" onClick={() => handleDeleteUser(user._id)} sx={actionButtonSx("error")}>
                                    <TrashIcon size={16} />
                                  </IconButton>
                                </Tooltip>
                              )
                            )}
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })}
              </Stack>
            ) : (
            <ScrollableTableContainer
              showControls={false}
              sx={{
                flex: 1,
                // The page itself scrolls. Keep all 10 default rows visible
                // before pagination instead of clipping them inside a short
                // viewport-dependent table scroller.
                maxHeight: "none",
                overflowY: "visible",
                scrollbarWidth: "thin",
                scrollbarColor: `${alpha("#64748b", 0.45)} transparent`,
                "&::-webkit-scrollbar": { height: 8 },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: alpha("#64748b", 0.4),
                  borderRadius: 999,
                },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {(
                      isRoleScopedPage
                        ? [
                            { key: "user", label: "User" },
                            { key: "mobileNo", label: "Phone" },
                            { key: "email", label: "Email" },
                            { key: "department", label: bucketLabel },
                            { key: "uniqueId", label: "Login ID" },
                            { key: "password", label: "Password" },
                            { key: "status", label: "Status" },
                            { key: "account", label: "Account" },
                            { key: "createdBy", label: "Created By" },
                            { key: "updatedAt", label: "Last Login" },
                            { key: "createdAt", label: "Created On" },
                          ]
                        : [
                            { key: "user", label: "User" },
                            { key: "role", label: "Role" },
                            { key: "uniqueId", label: "Login ID" },
                            { key: "mobileNo", label: "Phone" },
                            { key: "password", label: "Password" },
                            { key: "status", label: "Status" },
                            { key: "account", label: "Account" },
                            { key: "updatedAt", label: "Last Login" },
                            { key: "createdAt", label: "Created On" },
                          ]
                    ).map((column) => (
                      <TableCell
                        key={column.key}
                        sx={{
                          fontWeight: 800,
                          fontSize: 13,
                          color: "#64748b",
                          bgcolor: "#fff",
                          borderBottom: `1px solid ${alpha("#0f172a", 0.07)}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {["uniqueId", "mobileNo", "updatedAt", "createdAt", "role", "email", "department"].includes(column.key) ? (
                          <TableSortLabel
                            active={orderBy === (column.key === "status" ? "isActive" : column.key)}
                            direction={orderBy === (column.key === "status" ? "isActive" : column.key) ? order : "asc"}
                            onClick={() => onRequestSort(column.key === "department" ? "department" : column.key === "status" ? "isActive" : column.key)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 800, fontSize: 13, color: "#64748b", bgcolor: "#fff", borderBottom: `1px solid ${alpha("#0f172a", 0.07)}` }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sortedUsers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((user) => {
                      const meta = getRoleMeta(user.role);
                      const permissionLabels = collectPermissionLabels(user.permissions);
                      return (
                      <TableRow
                        key={unwrapMongoValue(user._id) || user._id}
                        hover
                        sx={{
                          transition: "background-color 0.18s ease, transform 0.18s ease",
                          "&:hover": {
                            bgcolor: alpha("#2563eb", 0.025),
                          },
                        }}
                      >
                        <TableCell sx={{ py: 1.8, minWidth: 260, borderBottom: `1px solid ${alpha("#0f172a", 0.05)}` }}>
                          <Stack direction="row" spacing={1.4} alignItems="center">
                            <Avatar sx={{ width: 44, height: 44, bgcolor: alpha(meta.tone, 0.12), color: meta.tone, fontWeight: 800 }}>
                              {getInitials(user.username)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 800, color: "#0f172a" }} noWrap>
                                {user.username || user.name || "-"}
                              </Typography>
                              <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.35 }} noWrap>
                                {isRoleScopedPage ? (user.uniqueId || user.email || "-") : (user.email || "-")}
                              </Typography>
                              {!isRoleScopedPage ? (
                                <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap sx={{ mt: 0.8 }}>
                                  {(user.remainingTokens || user.licenseSummary?.totalRemainingCredits) ? (
                                    <Chip
                                      size="small"
                                      label={`Tokens: ${Number(user.remainingTokens ?? user.licenseSummary?.totalRemainingCredits ?? 0)}`}
                                      sx={{ height: 24, borderRadius: 2, fontWeight: 700, bgcolor: alpha("#2563eb", 0.08), color: "#2563eb" }}
                                    />
                                  ) : null}
                                  {permissionLabels.length > 0 ? (
                                    <Chip
                                      size="small"
                                      label={`${permissionLabels.length} permissions`}
                                      sx={{ height: 24, borderRadius: 2, fontWeight: 700, bgcolor: alpha("#0f172a", 0.05), color: "#475569" }}
                                    />
                                  ) : null}
                                  {Array.isArray(user.managedUsers) && user.managedUsers.length > 0 ? (
                                    <Chip
                                      size="small"
                                      label={`${user.managedUsers.length} managed users`}
                                      sx={{ height: 24, borderRadius: 2, fontWeight: 700, bgcolor: alpha("#16a34a", 0.08), color: "#15803d" }}
                                    />
                                  ) : null}
                                </Stack>
                              ) : null}
                            </Box>
                          </Stack>
                        </TableCell>
                        {!isRoleScopedPage ? (
                          <TableCell sx={{ borderBottom: `1px solid ${alpha("#0f172a", 0.05)}` }}>
                            <Chip size="small" label={roleLabel(user.role)} sx={{ bgcolor: alpha(meta.tone, 0.12), color: meta.tone, fontWeight: 800, borderRadius: 2 }} />
                          </TableCell>
                        ) : null}
                        {!isRoleScopedPage ? (
                          <TableCell sx={{ fontWeight: 700, color: "#334155", borderBottom: `1px solid ${alpha("#0f172a", 0.05)}` }}>{user.uniqueId || "-"}</TableCell>
                        ) : null}
                        <TableCell sx={{ fontWeight: 600, color: "#334155", borderBottom: `1px solid ${alpha("#0f172a", 0.05)}` }}>{user.mobileNo || "-"}</TableCell>
                        {isRoleScopedPage ? (
                          <TableCell sx={{ fontWeight: 600, color: "#334155", borderBottom: `1px solid ${alpha("#0f172a", 0.05)}` }}>{user.email || "-"}</TableCell>
                        ) : null}
                        {isRoleScopedPage ? (
                          <TableCell sx={{ fontWeight: 600, color: "#334155", borderBottom: `1px solid ${alpha("#0f172a", 0.05)}` }}>{resolveUserBucket(user)}</TableCell>
                        ) : null}
                        {isRoleScopedPage ? (
                          <TableCell sx={{ fontWeight: 700, color: "#334155", borderBottom: `1px solid ${alpha("#0f172a", 0.05)}` }}>{user.uniqueId || "-"}</TableCell>
                        ) : null}
                        <TableCell sx={{ minWidth: 150, borderBottom: `1px solid ${alpha("#0f172a", 0.05)}` }}>
                          {renderPasswordCell(user)}
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${alpha("#0f172a", 0.05)}` }}>
                          <Chip
                            size="small"
                            label={user.isDeleted ? "Deleted" : user.isActive === false ? "Inactive" : "Active"}
                            sx={{
                              borderRadius: 999,
                              fontWeight: 800,
                              bgcolor: user.isDeleted ? alpha("#ef4444", 0.12) : user.isActive === false ? alpha("#f59e0b", 0.12) : alpha("#22c55e", 0.12),
                              color: user.isDeleted ? "#ef4444" : user.isActive === false ? "#d97706" : "#16a34a",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 140, borderBottom: `1px solid ${alpha("#0f172a", 0.05)}` }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Switch
                              checked={user.isActive === false}
                              onChange={(event) => handleToggleDisabled(user, event.target.checked)}
                              disabled={
                                user.isDeleted ||
                                !userActions.canEdit ||
                                (String(user._id) === String(currentUserId) && currentUserRole === ROLES.SUPER_ADMIN)
                              }
                            />
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>
                              {user.isDeleted ? "Deleted" : user.isActive === false ? "Disabled" : "Enabled"}
                            </Typography>
                          </Stack>
                        </TableCell>
                        {isRoleScopedPage ? (
                          <TableCell sx={{ color: "#334155", borderBottom: `1px solid ${alpha("#0f172a", 0.05)}` }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{resolveCreatedByLabel(user)}</Typography>
                          </TableCell>
                        ) : null}
                        <TableCell sx={{ color: "#334155", borderBottom: `1px solid ${alpha("#0f172a", 0.05)}` }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{formatDateTimeCompact(user.updatedAt)}</Typography>
                          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.35 }}>{formatRelativeShort(user.updatedAt)}</Typography>
                        </TableCell>
                        <TableCell sx={{ color: "#334155", borderBottom: `1px solid ${alpha("#0f172a", 0.05)}` }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{formatDateTimeCompact(user.createdAt)}</Typography>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            minWidth: 420,
                            whiteSpace: "nowrap",
                            borderBottom: `1px solid ${alpha("#0f172a", 0.05)}`,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.8}
                            justifyContent="flex-end"
                            alignItems="center"
                            flexWrap="nowrap"
                            sx={{ width: "100%" }}
                          >
                            {isRoleScopedPage && (
                              <Tooltip title="View">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setEditingUser({
                                      ...user,
                                      isActive: user.isActive ?? true,
                                      devicePermissions: user.devicePermissions || { create: false, read: false, update: false, delete: false, command: false },
                                      userPermissions: user.userPermissions || { create: false, read: false, update: false, delete: false, assign: false },
                                    })
                                  }
                                  sx={actionTileSx("info")}
                                >
                                  <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {!user.isDeleted && userActions.canLoginAsUser && (
                              <Tooltip title="Login as User">
                                <IconButton
                                  size="small"
                                  onClick={() => handleLoginAsUser(user._id)}
                                  sx={actionTileSx("slate")}
                                >
                                  <LogIn size={16} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {!user.isDeleted && (
                              <Tooltip title="Notification Alerts">
                                <IconButton
                                  size="small"
                                  onClick={() => setNotificationDialogUser(user)}
                                  sx={actionTileSx("warning")}
                                >
                                  <NotificationsActiveOutlinedIcon sx={{ fontSize: 19 }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {!user.isDeleted && canAssignDevice && (
                              <Tooltip title="Assign Device">
                                <IconButton
                                  size="small"
                                  onClick={() => openAssignDeviceModal(user)}
                                  sx={actionTileSx("primary")}
                                >
                                  <FleetVehicleIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {!user.isDeleted && canAssignUserToGA && (
                              <Tooltip title="Link User To Group">
                                <IconButton
                                  size="small"
                                  onClick={() => openAssignGroupModal(user)}
                                  sx={actionTileSx("info")}
                                >
                                  <FleetUserAssignIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {userActions.canEdit && (
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setEditingUser({
                                      ...user,
                                      isActive: user.isActive ?? true,
                                      devicePermissions: user.devicePermissions || {
                                        create: false,
                                        read: false,
                                        update: false,
                                        delete: false,
                                        command: false,
                                      },
                                      userPermissions: user.userPermissions || {
                                        create: false,
                                        read: false,
                                        update: false,
                                        delete: false,
                                        assign: false,
                                      },
                                    })
                                  }
                                  sx={actionTileSx("primary")}
                                >
                                  <EditIcon size={16} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {userActions.canResetPassword && (
                              <Tooltip title="Reset Password">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setResetUserId(user._id);
                                    setShowResetPasswordModal(true);
                                  }}
                                  sx={actionTileSx("warning")}
                                >
                                  <KeyRound size={16} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {user.isDeleted ? (
                              userActions.canRestore && (
                                <Tooltip title="Restore">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRestoreUser(user._id)}
                                    sx={actionTileSx("success")}
                                  >
                                    <RestoreIcon size={16} />
                                  </IconButton>
                                </Tooltip>
                              )
                            ) : (
                              userActions.canDelete && (
                                <Tooltip title="Delete">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteUser(user._id)}
                                      disabled={user._id === currentUserId && currentUserRole === ROLES.SUPER_ADMIN}
                                      sx={{
                                        ...actionTileSx("error"),
                                        "&.Mui-disabled": {
                                          color: alpha(theme.palette.error.main, 0.45),
                                          backgroundColor: alpha(theme.palette.error.main, 0.05),
                                          borderColor: alpha(theme.palette.error.main, 0.08),
                                        },
                                      }}
                                    >
                                      <TrashIcon size={16} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )})}
                </TableBody>
              </Table>
            </ScrollableTableContainer>
            )}

            <Divider />

            <TablePagination
              component="div"
              sx={{
                flexShrink: 0,
                minHeight: 56,
                borderTop: `1px solid ${alpha("#0f172a", 0.06)}`,
                bgcolor: "#fff",
              }}
              count={sortedUsers.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
              showFirstButton
              showLastButton
              labelRowsPerPage="Rows per page"
            />
          </>
        ) : (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <Typography sx={{ fontWeight: 1000, fontSize: 18 }}>No users found</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.7 }}>
              Try adjusting your search or filters.
            </Typography>
          </Box>
        )}
      </Paper>

      <Menu
        anchorEl={createAnchorEl}
        open={createMenuOpen}
        onClose={() => setCreateAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 3,
            minWidth: 280,
            border: `1px solid ${alpha("#0f172a", 0.08)}`,
            boxShadow: "0 20px 42px rgba(15, 23, 42, 0.12)",
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setCreateAnchorEl(null);
            setShowCreateUserModal?.(true);
          }}
        >
          <Stack spacing={0.3}>
            <Typography sx={{ fontWeight: 800, color: "#0f172a" }}>Open default user form</Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Use the existing user creation workflow.</Typography>
          </Stack>
        </MenuItem>
        <Divider />
        {creatableRoles.map((role) => {
          const meta = getRoleMeta(role);
          const Icon = meta.icon;
          return (
            <MenuItem
              key={role}
              onClick={() => {
                setCreateAnchorEl(null);
                if ([ROLES.SUB_ADMIN, ROLES.DEALER, ROLES.USER, ROLES.DRIVER, ROLES.CLIENT, ROLES.VIEWER, ROLES.MANAGER, ROLES.HR, ROLES.OFFICE_EMPLOYEE, ROLES.FIELD_EMPLOYEE].includes(normalizeRole(role))) {
                  setRoleModalRole(role);
                } else {
                  setShowCreateUserModal?.(true);
                }
              }}
            >
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(meta.tone, 0.12), color: meta.tone }}>
                  <Icon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 800, color: "#0f172a" }}>{meta.title}</Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{meta.description}</Typography>
                </Box>
              </Stack>
            </MenuItem>
          );
        })}
      </Menu>

      <RoleBasedUserFormModal
        role={roleModalRole}
        open={Boolean(roleModalRole)}
        onClose={() => setRoleModalRole("")}
        currentUser={currentUser}
        currentUserRole={currentUserRole}
      />

      <Dialog
        open={Boolean(assignDeviceDialogUser)}
        onClose={resetAssignDeviceDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            minHeight: { xs: "100dvh", md: "82vh" },
            maxHeight: { xs: "100dvh", md: "88vh" },
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 28px 80px rgba(15, 23, 42, 0.16)",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 2.2,
            borderBottom: `1px solid ${alpha("#0f172a", 0.08)}`,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            bgcolor: "#fff",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: { xs: 28, md: 20 }, fontWeight: 800, color: "#111827" }}>
              Assign Devices
            </Typography>
            <Typography sx={{ mt: 0.7, fontSize: 14, color: "#667085", fontWeight: 500 }}>
              Tick devices to assign them instantly. Untick to remove assignment instantly.
            </Typography>
          </Box>
          <IconButton onClick={resetAssignDeviceDialog}>
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            bgcolor: "#f8fbff",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: { xs: 1.5, md: 2.5 }, overflowY: "auto", flex: 1 }}>
            <Stack spacing={2}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 3,
                  borderColor: alpha("#0f172a", 0.08),
                  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.04)",
                }}
              >
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: { md: 250 } }}>
                    <Avatar sx={{ width: 52, height: 52, bgcolor: alpha("#2563eb", 0.12), color: "#2563eb", fontWeight: 800 }}>
                      {getInitials(assignDeviceDialogUser?.username)}
                    </Avatar>
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontSize: 17, fontWeight: 800, color: "#0f172a" }}>
                          {assignDeviceDialogUser?.username || "-"}
                        </Typography>
                        <Chip
                          size="small"
                          label={roleLabel(assignDeviceDialogUser?.role)}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 800,
                            bgcolor: alpha(getRoleMeta(assignDeviceDialogUser?.role).tone, 0.12),
                            color: getRoleMeta(assignDeviceDialogUser?.role).tone,
                          }}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                  <Grid container spacing={1.5} sx={{ flex: 1 }}>
                    {[
                      { label: "User ID", value: assignDeviceDialogUser?.uniqueId || "-" },
                      { label: "Mobile", value: assignDeviceDialogUser?.mobileNo || "-" },
                      { label: "Device Limit", value: Number(assignDeviceDialogUser?.permissions?.limits?.deviceLimit || 0).toLocaleString() },
                      { label: "Available Licenses", value: Number(assignDeviceDialogUser?.licenseSummary?.yearlyAvailableLicenses ?? assignDeviceDialogUser?.licenseSummary?.totalRemainingCredits ?? assignDeviceDialogUser?.remainingTokens ?? 0).toLocaleString() },
                      { label: "User Limit", value: Number(assignDeviceDialogUser?.permissions?.limits?.userLimit || 0).toLocaleString() },
                      { label: "Status", value: assignDeviceDialogUser?.isActive === false ? "Inactive" : "Active", chip: true },
                    ].map((item) => (
                      <Grid key={item.label} item xs={6} md={2}>
                        <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.35 }}>{item.label}</Typography>
                        {item.chip ? (
                          <Chip
                            size="small"
                            label={item.value}
                            sx={{
                              borderRadius: 999,
                              fontWeight: 800,
                              bgcolor: assignDeviceDialogUser?.isActive === false ? alpha("#94a3b8", 0.16) : alpha("#22c55e", 0.12),
                              color: assignDeviceDialogUser?.isActive === false ? "#64748b" : "#16a34a",
                            }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>{item.value}</Typography>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              </Paper>

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
                }}
              >
                {[
                  { label: "Total Devices", value: assignDeviceStats.total, color: "#2563eb" },
                  { label: "Assigned", value: assignDeviceStats.assigned, color: "#16a34a" },
                  { label: "Unassigned", value: assignDeviceStats.unassigned, color: "#f59e0b" },
                  { label: "Selected Now", value: assignDeviceStats.selectedNow, color: "#7c3aed" },
                ].map((card) => (
                  <Paper
                    key={card.label}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      borderColor: alpha(card.color, 0.12),
                      boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: "#64748b", fontWeight: 600 }}>{card.label}</Typography>
                    <Typography sx={{ mt: 0.6, fontSize: 34, lineHeight: 1, fontWeight: 800, color: "#0f172a" }}>
                      {card.value}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 3,
                  borderColor: alpha("#0f172a", 0.08),
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: "column", xl: "row" }} spacing={1.2}>
                    <TextField
                      value={assignDeviceSearch}
                      onChange={(event) => setAssignDeviceSearch(event.target.value)}
                      placeholder="Search by vehicle no, IMEI, SIM no or model"
                      sx={{ flex: 1 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search size={18} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {[
                        { key: "all", label: "All Devices" },
                        { key: "assigned", label: "Assigned" },
                        { key: "unassigned", label: "Unassigned" },
                        { key: "running", label: "Running" },
                        { key: "stopped", label: "Stopped" },
                        { key: "no_reporting", label: "No Reporting" },
                      ].map((filter) => (
                        <Button
                          key={filter.key}
                          variant={assignDeviceFilter === filter.key ? "contained" : "outlined"}
                          onClick={() => setAssignDeviceFilter(filter.key)}
                          sx={{
                            borderRadius: 2.5,
                            textTransform: "none",
                            fontWeight: 700,
                            px: 1.8,
                            boxShadow: "none",
                          }}
                        >
                          {filter.label}
                        </Button>
                      ))}
                    </Stack>
                  </Stack>

                  <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" spacing={1.2} alignItems={{ xs: "stretch", lg: "center" }}>
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" onClick={handleSelectAllVisible} sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 700 }}>
                        Select All Visible
                      </Button>
                      <Button variant="outlined" onClick={handleClearVisible} sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 700 }}>
                        Clear Visible
                      </Button>
                    </Stack>
                    <Stack direction="row" spacing={1.2} alignItems="center" justifyContent="space-between">
                      <Typography sx={{ fontSize: 14, color: "#64748b", fontWeight: 600 }}>
                        Showing {assignDeviceRows.length ? assignDevicePage * assignDeviceRowsPerPage + 1 : 0} to {Math.min((assignDevicePage + 1) * assignDeviceRowsPerPage, assignDeviceRows.length)} of {assignDeviceRows.length} devices
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select value={assignDeviceRowsPerPage} onChange={(e) => setAssignDeviceRowsPerPage(Number(e.target.value))}>
                          {[20, 50, 100].map((size) => (
                            <MenuItem key={size} value={size}>{size} per page</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  borderColor: alpha("#0f172a", 0.08),
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <TableContainer sx={{ maxHeight: { xs: "44vh", md: "48vh" } }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "#fff", width: 58 }}>
                          <Checkbox
                            checked={visibleDeviceIds.length > 0 && assignDevicePagedRows.every((row) => row.checked)}
                            indeterminate={assignDevicePagedRows.some((row) => row.checked) && !assignDevicePagedRows.every((row) => row.checked)}
                            onChange={(event) => (event.target.checked ? handleSelectAllVisible() : handleClearVisible())}
                            disabled={assignDevicePagedRows.some((row) => pendingAssignIds.has(row.id))}
                          />
                        </TableCell>
                        {["Vehicle No", "IMEI", "Status", "Last Update", "SIM No", "Model", "Icon"].map((head) => (
                          <TableCell key={head} sx={{ bgcolor: "#fff", fontWeight: 800, color: "#475569", whiteSpace: "nowrap" }}>
                            {head}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assignDevicePagedRows.length ? assignDevicePagedRows.map((device) => (
                        <TableRow
                          key={device.id}
                          hover
                          sx={{
                            bgcolor: device.checked ? alpha("#2563eb", 0.08) : "#fff",
                            "&:hover": {
                              bgcolor: device.checked ? alpha("#2563eb", 0.12) : alpha("#0f172a", 0.02),
                            },
                          }}
                        >
                          <TableCell>
                            <Checkbox
                              checked={device.checked}
                              onChange={(event) => handleToggleAssignDevice(device, event.target.checked)}
                              disabled={pendingAssignIds.has(device.id)}
                              sx={{ transform: "scale(1.08)" }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: "#0f172a" }}>{device.name}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{device.imei}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={device.statusLabel}
                              sx={{
                                borderRadius: 999,
                                fontWeight: 800,
                                bgcolor: device.statusBg,
                                color: device.statusColor,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>{device.lastUpdate}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{device.simNo}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{device.model}</TableCell>
                          <TableCell>
                            <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(device.statusColor, 0.1), color: device.statusColor }}>
                              <FleetVehicleIcon sx={{ fontSize: 18 }} />
                            </Avatar>
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 6, color: "#64748b", fontWeight: 600 }}>
                            No devices found for the selected filters.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 2,
            borderTop: `1px solid ${alpha("#0f172a", 0.08)}`,
            bgcolor: "#fff",
            position: "sticky",
            bottom: 0,
            zIndex: 2,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.2}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            sx={{ width: "100%" }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
              <Typography sx={{ fontSize: 13, color: "#64748b", fontWeight: 600 }}>
                Device assignment updates apply instantly when you tick or untick.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={assignDevicePage === 0}
                  onClick={() => setAssignDevicePage((prev) => Math.max(0, prev - 1))}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={(assignDevicePage + 1) * assignDeviceRowsPerPage >= assignDeviceRows.length}
                  onClick={() => setAssignDevicePage((prev) => prev + 1)}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                >
                  Next
                </Button>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1.2} justifyContent="flex-end">
              <Button variant="outlined" onClick={resetAssignDeviceDialog} sx={{ minWidth: 128, borderRadius: 2.5, textTransform: "none", fontWeight: 700 }}>
                Close
              </Button>
            </Stack>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignGroupModalOpen}
        onClose={() => setAssignGroupModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 1000 }}>Add User to Group</DialogTitle>
        <form
          onSubmit={(e) => {
            handleAssignUserToGroupAdmin?.(e);
          }}
        >
          <DialogContent sx={{ pt: 1.5 }}>
            <Stack spacing={2}>
              <Autocomplete
                options={assignableUsersForGroupAdmin}
                getOptionLabel={(u) => `${u?.username || u?.name || "User"}`}
                value={
                  assignableUsersForGroupAdmin.find(
                    (u) => String(u?._id) === String(assignUserToGroupAdminUserId)
                  ) || null
                }
                onChange={(_, value) => {
                  setAssignUserToGroupAdminUserId?.(value?._id || "");
                  setAssignUserSearchQuery?.(value?.username || value?.name || "");
                }}
                inputValue={assignUserSearchQuery || ""}
                onInputChange={(_, value, reason) => {
                  if (reason === "input") {
                    setAssignUserSearchQuery?.(value);
                    setAssignUserToGroupAdminUserId?.("");
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label={assignmentLabels?.userLabel || "Select User"} required />
                )}
              />

              <Autocomplete
                options={groupAdmins}
                getOptionLabel={(u) => `${u?.username || u?.name || "Admin"}`}
                value={groupAdmins.find((u) => String(u?._id) === String(assignUserToGroupAdminTargetId)) || null}
                onChange={(_, value) => {
                  setAssignUserToGroupAdminTargetId?.(value?._id || "");
                  setGroupAdminSearchQuery?.(value?.username || value?.name || "");
                }}
                inputValue={groupAdminSearchQuery || ""}
                onInputChange={(_, value, reason) => {
                  if (reason === "input") {
                    setGroupAdminSearchQuery?.(value);
                    setAssignUserToGroupAdminTargetId?.("");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={assignmentLabels?.adminLabel || "Select Admin/Dealer"}
                    placeholder={assignmentLabels?.adminPlaceholder || ""}
                    required
                  />
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={() => setAssignGroupModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!assignUserToGroupAdminUserId || !assignUserToGroupAdminTargetId}
            >
              Link to Group
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ManagedUserNotificationDialog
        open={Boolean(notificationDialogUser)}
        onClose={() => setNotificationDialogUser(null)}
        targetUser={notificationDialogUser}
        devices={safeDevices}
        showStatus={showStatus}
        title="User Notifications"
      />

    </Box>
  );
}

/** =========================
 * EDIT USER MODAL (same features)
 * ========================= */
function EditUserModal({
  editingUser,
  setEditingUser,
  handleEditUser,
  currentUser,
  currentUserId,
  currentUserRole,
  creatableRoles,
}) {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const [showAdvanced, setShowAdvanced] = useState(false);

  if (!editingUser) return null;

  if (normalizeRole(editingUser.role) === ROLES.DEALER) {
    return (
      <AddDealerModal
        open={Boolean(editingUser)}
        onClose={() => setEditingUser(null)}
        onSuccess={() => setEditingUser(null)}
        mode="edit"
        initialData={editingUser}
      />
    );
  }

  if (normalizeRole(editingUser.role) === ROLES.SUB_ADMIN) {
    return (
      <AddSubAdminModal
        open={Boolean(editingUser)}
        onClose={() => setEditingUser(null)}
        onSuccess={() => setEditingUser(null)}
        mode="edit"
        initialData={editingUser}
      />
    );
  }

  if ([ROLES.USER, ROLES.DRIVER, ROLES.CLIENT, ROLES.VIEWER].includes(normalizeRole(editingUser.role))) {
    const normalizedRole = normalizeRole(editingUser.role);
    return (
      <AddUserModal
        open={Boolean(editingUser)}
        onClose={() => setEditingUser(null)}
        onSuccess={() => setEditingUser(null)}
        mode="edit"
        initialData={editingUser}
        forcedRole={normalizedRole}
        title={`Edit ${roleLabel(normalizedRole)}`}
      />
    );
  }

  if (
    [
      ROLES.MANAGER,
      ROLES.HR,
      ROLES.OFFICE_EMPLOYEE,
      ROLES.FIELD_EMPLOYEE,
    ].includes(normalizeRole(editingUser.role))
  ) {
    return (
      <CreateUserModal
        setShowCreateUserModal={(value) => {
          if (!value) setEditingUser(null);
        }}
        currentUserRole={currentUserRole}
        currentUser={currentUser}
        mode="edit"
        initialData={editingUser}
        onSuccess={() => setEditingUser(null)}
      />
    );
  }

  return (
    <Dialog
      open={Boolean(editingUser)}
      onClose={() => setEditingUser(null)}
      fullWidth
      maxWidth="md"
      fullScreen={isSmDown}
      PaperProps={{ sx: { borderRadius: isSmDown ? 0 : 4 } }}
    >
      <DialogTitle sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}` }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontWeight: 1000 }}>Edit User Details</Typography>
          <IconButton onClick={() => setEditingUser(null)}>
            <X size={20} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <form onSubmit={(e) => handleEditUser(e, editingUser._id)}>
        <DialogContent sx={{ p: 2.25 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="User Name"
                value={editingUser.username}
                onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Email Address"
                type="email"
                value={editingUser.email}
                onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Mobile Number"
                value={editingUser.mobileNo || ""}
                onChange={(e) => setEditingUser({ ...editingUser, mobileNo: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", mb: 0.6 }}>
                  Role
                </Typography>
                <Select
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                  disabled={editingUser._id === currentUserId && currentUserRole === ROLES.SUPER_ADMIN}
                >
                  {creatableRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {roleLabel(role)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Button
            type="button"
            variant="text"
            onClick={() => setShowAdvanced((p) => !p)}
            sx={{ fontWeight: 1000, textTransform: "none" }}
          >
            {showAdvanced ? "Hide Advanced Settings" : "Show Advanced Settings"}
          </Button>

          {showAdvanced && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 1.8, borderRadius: 3 }}>
                    <Typography sx={{ fontWeight: 1000, mb: 1 }}>Account Status</Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={editingUser.isActive ? "active" : "disabled"}
                        onChange={(e) =>
                          setEditingUser({ ...editingUser, isActive: e.target.value === "active" })
                        }
                        disabled={editingUser._id === currentUser?._id && currentUser?.role === ROLES.SUPER_ADMIN}
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="disabled">Disabled</MenuItem>
                      </Select>
                    </FormControl>
                  </Paper>
                </Grid>

                {/* ✅ Permissions UI still in edit modal */}
                <Grid item xs={12} md={8}>
                  <Paper variant="outlined" sx={{ p: 1.8, borderRadius: 3 }}>
                    <Typography sx={{ fontWeight: 1000, mb: 1 }}>Device Permissions</Typography>
                    <FormGroup row>
                      {["create", "read", "update", "delete", "command"].map((perm) => (
                        <FormControlLabel
                          key={perm}
                          control={
                            <Checkbox
                              checked={Boolean(editingUser.devicePermissions?.[perm])}
                              onChange={(e) =>
                                setEditingUser({
                                  ...editingUser,
                                  devicePermissions: {
                                    ...editingUser.devicePermissions,
                                    [perm]: e.target.checked,
                                  },
                                })
                              }
                            />
                          }
                          label={perm.charAt(0).toUpperCase() + perm.slice(1)}
                        />
                      ))}
                    </FormGroup>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 1.8, borderRadius: 3, mt: 2 }}>
                    <Typography sx={{ fontWeight: 1000, mb: 1 }}>User Management Permissions</Typography>
                    <FormGroup row>
                      {["create", "read", "update", "delete", "assign"].map((perm) => (
                        <FormControlLabel
                          key={perm}
                          control={
                            <Checkbox
                              checked={Boolean(editingUser.userPermissions?.[perm])}
                              onChange={(e) =>
                                setEditingUser({
                                  ...editingUser,
                                  userPermissions: {
                                    ...editingUser.userPermissions,
                                    [perm]: e.target.checked,
                                  },
                                })
                              }
                            />
                          }
                          label={perm.charAt(0).toUpperCase() + perm.slice(1)}
                        />
                      ))}
                    </FormGroup>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.9)}` }}>
          <Button variant="outlined" onClick={() => setEditingUser(null)} sx={{ borderRadius: 2, fontWeight: 900 }}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" sx={{ borderRadius: 2, fontWeight: 900 }}>
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default UserManagement;
function RoleBasedUserFormModal({
  role,
  open,
  onClose,
  currentUser,
  currentUserRole,
}) {
  if (!open || !role) return null;

  const normalized = normalizeRole(role);

  if (normalized === ROLES.DEALER) {
    return <AddDealerModal open={open} onClose={onClose} onSuccess={onClose} mode="create" />;
  }

  if (normalized === ROLES.SUB_ADMIN) {
    return <AddSubAdminModal open={open} onClose={onClose} onSuccess={onClose} mode="create" />;
  }

  if ([ROLES.USER, ROLES.DRIVER, ROLES.CLIENT, ROLES.VIEWER].includes(normalized)) {
    return (
      <AddUserModal
        open={open}
        onClose={onClose}
        onSuccess={onClose}
        mode="create"
        forcedRole={normalized}
        title={`Add ${roleLabel(normalized)}`}
        subtitle={`Create a new ${roleLabel(normalized).toLowerCase()} account in 3 simple steps.`}
      />
    );
  }

  if ([ROLES.MANAGER, ROLES.HR, ROLES.OFFICE_EMPLOYEE, ROLES.FIELD_EMPLOYEE].includes(normalized)) {
    return (
      <CreateUserModal
        setShowCreateUserModal={(value) => {
          if (!value) onClose();
        }}
        currentUserRole={currentUserRole}
        currentUser={currentUser}
        mode="create"
        initialData={{ role: normalized }}
        onSuccess={onClose}
      />
    );
  }

  return null;
}

function CreateUserHubModal({
  open,
  onClose,
  users = [],
  creatableRoles = [],
  currentUser,
  currentUserRole,
  handleDeleteUser,
  handleRestoreUser,
  onDownloadUsersTemplate,
  onUsersFileSelected,
  setResetUserId,
  setShowResetPasswordModal,
  onRefresh,
  canLoginAsUser = true,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [impersonateUser] = useImpersonateUserMutation();
  const uploadRef = React.useRef(null);
  const [roleTab, setRoleTab] = useState("");
  const [searchName, setSearchName] = useState("");
  const [searchLoginId, setSearchLoginId] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [createRole, setCreateRole] = useState("");
  const [editingHubUser, setEditingHubUser] = useState(null);

  const roleTabs = useMemo(() => {
    const normalizedRoles = Array.from(
      new Set((creatableRoles || []).map((role) => normalizeRole(role)).filter(Boolean))
    );
    const preferredOrder = [
      ROLES.MANAGER,
      ROLES.HR,
      ROLES.OFFICE_EMPLOYEE,
      ROLES.FIELD_EMPLOYEE,
    ];
    return normalizedRoles
      .filter((role) => preferredOrder.includes(role))
      .sort((a, b) => {
      const aIndex = preferredOrder.indexOf(a);
      const bIndex = preferredOrder.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return roleLabel(a).localeCompare(roleLabel(b));
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
      });
  }, [creatableRoles]);

  useEffect(() => {
    if (!open) return;
    setRoleTab((prev) => prev || roleTabs[0] || "");
    setSearchName("");
    setSearchLoginId("");
    setSearchPhone("");
    setPage(0);
  }, [open, roleTabs]);

  const filteredUsers = useMemo(() => {
    const nameQuery = searchName.trim().toLowerCase();
    const loginQuery = searchLoginId.trim().toLowerCase();
    const phoneQuery = searchPhone.trim();
    return (Array.isArray(users) ? users : [])
      .filter((user) => !roleTab || normalizeRole(user?.role) === roleTab)
      .filter((user) => {
        const name = String(user?.username || "").toLowerCase();
        const login = String(user?.uniqueId || "").toLowerCase();
        const phone = String(user?.mobileNo || "");
        if (nameQuery && !name.includes(nameQuery)) return false;
        if (loginQuery && !login.includes(loginQuery)) return false;
        if (phoneQuery && !phone.includes(phoneQuery)) return false;
        return true;
      });
  }, [users, roleTab, searchName, searchLoginId, searchPhone]);

  const roleCounts = useMemo(() => {
    return roleTabs.reduce((acc, role) => {
      acc[role] = (Array.isArray(users) ? users : []).filter((user) => normalizeRole(user?.role) === role).length;
      return acc;
    }, {});
  }, [roleTabs, users]);

  const pagedUsers = useMemo(
    () => filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredUsers, page, rowsPerPage]
  );

  const openCreateForCurrentRole = () => {
    if (!roleTab) return;
    setCreateRole(roleTab);
  };

  const handleHubLoginAsUser = async (targetUserId) => {
    if (!canLoginAsUser) {
      alert("You don't have permission to login as other users.");
      return;
    }

    if (
      !window.confirm(
        "Are you sure you want to log in as this user? You will be logged out of Admin."
      )
    ) {
      return;
    }

    try {
      const currentAdminToken = localStorage.getItem("token");
      if (currentAdminToken) localStorage.setItem("adminFallbackToken", currentAdminToken);

      const response = await impersonateUser(targetUserId).unwrap();
      dispatch(adminApi.util.resetApiState());
      dispatch(loginSuccess({ user: response.user, token: response.token }));
      navigate("/dashboard", { replace: true, state: { __impersonate: true, t: Date.now() } });
    } catch (error) {
      alert(error?.data?.msg || "Failed to login as user.");
    }
  };

  const handleEditFromHub = (user) => {
    setEditingHubUser(user);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <DialogTitle sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontWeight: 1000, fontSize: 22 }}>User Control Center</Typography>
            <IconButton onClick={onClose}>
              <X size={20} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 0, bgcolor: "grey.50" }}>
          <Box sx={{ p: 2.5 }}>
            <Stack spacing={2}>
              <Tabs
                value={roleTab}
                onChange={(_, value) => {
                  setRoleTab(value);
                  setPage(0);
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 42,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 900,
                    minHeight: 42,
                    px: 2,
                    borderRadius: 2,
                  },
                  "& .MuiTabs-flexContainer": { gap: 1 },
                }}
              >
                {roleTabs.map((role) => {
                  const meta = CREATE_ROLE_CATALOG[role];
                  const Icon = meta?.icon;
                  return (
                    <Tab
                      key={role}
                      value={role}
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          {Icon ? <Icon sx={{ fontSize: 16 }} /> : null}
                          <Typography component="span" sx={{ fontWeight: 900 }}>
                            {roleLabel(role)}
                          </Typography>
                          <Chip
                            size="small"
                            label={roleCounts[role] ?? 0}
                            sx={{ height: 22, fontWeight: 900, borderRadius: 1.5 }}
                          />
                        </Stack>
                      }
                      sx={{
                        bgcolor: roleTab === role ? alpha(theme.palette.primary.main, 0.12) : "transparent",
                        border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                      }}
                    />
                  );
                })}
              </Tabs>

              <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} alignItems={{ xs: "stretch", lg: "center" }}>
                <TextField
                  placeholder="Search by name"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search size={16} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  placeholder="Search by Login Id"
                  value={searchLoginId}
                  onChange={(e) => setSearchLoginId(e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search size={16} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  placeholder="Search by phone"
                  value={searchPhone}
                  onChange={(e) => setSearchPhone(e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search size={16} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Stack direction="row" spacing={1}>
                  <Tooltip title="Add New">
                    <IconButton onClick={openCreateForCurrentRole} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.7)}`, borderRadius: 2 }}>
                      <Plus size={18} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Import Users">
                    <IconButton onClick={() => uploadRef.current?.click()} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.7)}`, borderRadius: 2 }}>
                      <Upload size={18} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export Template">
                    <IconButton onClick={onDownloadUsersTemplate} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.7)}`, borderRadius: 2 }}>
                      <Download size={18} />
                    </IconButton>
                  </Tooltip>
                  <input ref={uploadRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={onUsersFileSelected} />
                </Stack>
              </Stack>

              <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
                <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`, bgcolor: alpha(theme.palette.common.black, 0.03) }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <FormControl size="small" sx={{ minWidth: 90 }}>
                      <Select
                        value={rowsPerPage}
                        onChange={(e) => {
                          setRowsPerPage(Number(e.target.value));
                          setPage(0);
                        }}
                      >
                        {[10, 20, 50].map((size) => (
                          <MenuItem key={size} value={size}>
                            {size}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Typography variant="body2" color="text.secondary">
                      {filteredUsers.length} records
                    </Typography>
                  </Stack>
                </Box>

                <ScrollableTableContainer sx={{ maxHeight: "60vh" }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 1000 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 1000 }}>User ID</TableCell>
                        <TableCell sx={{ fontWeight: 1000 }}>Mobile</TableCell>
                        <TableCell sx={{ fontWeight: 1000 }}>Role</TableCell>
                        <TableCell sx={{ fontWeight: 1000 }}>Remaining Tokens</TableCell>
                        <TableCell sx={{ fontWeight: 1000 }}>Status</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 1000 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedUsers.map((user) => (
                        <TableRow key={unwrapMongoValue(user._id) || user._id} hover>
                          <TableCell>{user.username || "-"}</TableCell>
                          <TableCell>{user.uniqueId || "-"}</TableCell>
                          <TableCell>{user.mobileNo || "-"}</TableCell>
                          <TableCell>{roleLabel(user.role)}</TableCell>
                          <TableCell>
                            <CreditsCell
                              credits={user.credits}
                              licenseSummary={user.licenseSummary}
                              remainingTokens={user.remainingTokens}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={user.isDeleted ? "Deleted" : user.isActive === false ? "Disabled" : "Active"}
                              color={user.isDeleted ? "error" : user.isActive === false ? "warning" : "success"}
                              sx={{ fontWeight: 800, borderRadius: 2 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.6} justifyContent="flex-end">
                              {!user.isDeleted && canLoginAsUser && (
                                <Tooltip title="Login as User">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleHubLoginAsUser(user._id)}
                                    sx={{ bgcolor: "#f8fafc", borderRadius: 1 }}
                                  >
                                    <LogIn size={16} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => handleEditFromHub(user)}>
                                  <EditIcon size={16} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Permissions">
                                <IconButton size="small" onClick={() => handleEditFromHub(user)}>
                                  <PermissionIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reset Password">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setResetUserId?.(user._id);
                                    setShowResetPasswordModal?.(true);
                                  }}
                                >
                                  <KeyRound size={16} />
                                </IconButton>
                              </Tooltip>
                              {user.isDeleted ? (
                                <Tooltip title="Restore">
                                  <IconButton size="small" onClick={() => handleRestoreUser?.(user._id)}>
                                    <RestoreIcon size={16} />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Delete">
                                  <IconButton size="small" onClick={() => handleDeleteUser?.(user._id)}>
                                    <TrashIcon size={16} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollableTableContainer>

                <Box sx={{ px: 2, py: 1.25, borderTop: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Showing {filteredUsers.length ? page * rowsPerPage + 1 : 0} to {Math.min((page + 1) * rowsPerPage, filteredUsers.length)} of {filteredUsers.length} entries
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                        Prev
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={(page + 1) * rowsPerPage >= filteredUsers.length}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        Next
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>

      <RoleBasedUserFormModal
        role={createRole}
        open={Boolean(createRole)}
        onClose={() => {
          setCreateRole("");
          onRefresh?.();
        }}
        currentUser={currentUser}
        currentUserRole={currentUserRole}
      />

      <EditUserModal
        editingUser={editingHubUser}
        setEditingUser={setEditingHubUser}
        handleEditUser={() => {}}
        currentUser={currentUser}
        currentUserId={currentUser?._id || currentUser?.id}
        currentUserRole={currentUserRole}
        creatableRoles={creatableRoles}
      />
    </>
  );
}

export { EditUserModal, CreateUserHubModal };
